import { useEffect, useState, type ChangeEvent } from 'react'
import { PageHeader, Modal, toast } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { uploadInvoiceFile, getInvoiceFileUrl } from '@/lib/invoiceFiles'
import { annualContribution, calcReservePlan, calcTax, marginalIncomeRate } from '@/lib/tax'

// ============================================================
// Utility helpers
// ============================================================

const fmtYen = (v: number) => '¥' + Math.round(v).toLocaleString()
const fmtDate = (s: string) => {
  if (!s) return ''
  const d = new Date(s)
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
}

// ============================================================
// Types
// ============================================================

interface Invoice { id: string; client_name: string; amount: number; invoice_date: string; paid_date?: string; due_date?: string; status: string; company_id?: string; file_url?: string | null; notes?: string; project_id?: string }
interface Expense { id: string; description: string; amount: number; expense_date: string; category: string; is_recurring?: boolean; recurring_status?: string; recurring_interval?: string; service_name?: string; is_deductible?: boolean }
interface Project { id: string; name: string; client_name: string; status: string; contract_type: string; default_rate?: number; budget?: number; start_date?: string; end_date?: string }
interface WishlistItem { id: string; title: string; amount: number; status: string; category?: string; priority?: string; url?: string; description?: string; purchased_at?: string }
interface TaxPayment { id: string; tax_type: string; due_date: string; amount: number; status: string; notes?: string }

// 売上は「入金月の前月」に計上する。
// 月末振り込みが多く、振り込み前の月＝実際に稼働した（売上が立つ）月に当たるため。
// 入金日があればその前月の YYYY-MM-01 を、入金日が無ければ請求日（≒稼働月）をそのまま使う。
// 返り値を前月の YYYY-MM-01 にしておくと、下流の月次/年次フィルタ（範囲比較）と substring 抽出が
// すべてそのまま成立する。
const revenueDate = (inv: { invoice_date: string; paid_date?: string | null }) => {
  if (!inv.paid_date) return inv.invoice_date
  const [y, m] = inv.paid_date.split('-').map(Number)
  const py = m === 1 ? y - 1 : y
  const pm = m === 1 ? 12 : m - 1
  return `${py}-${String(pm).padStart(2, '0')}-01`
}

// ============================================================
// Overview Tab
// ============================================================

const PROJ_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6']

function FinOverview() {
  // viewDate represents the month being viewed (always day=1)
  const [viewDate, setViewDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })
  const [data, setData] = useState<{ invoices: Invoice[]; expenses: Expense[]; projects: Project[]; allInv: Invoice[]; allExp: Expense[]; recurSubs: Expense[]; wishItems: WishlistItem[]; apiCostTotal: number } | null>(null)

  useEffect(() => {
    const year = viewDate.getFullYear()
    const month = viewDate.getMonth() + 1
    const ym = `${year}-${String(month).padStart(2, '0')}`
    const startOfMonth = `${ym}-01`
    const lastDay = new Date(year, month, 0).getDate()
    const endOfMonth = `${ym}-${String(lastDay).padStart(2, '0')}`
    const startOfYear = `${year}-01-01`
    const endOfYear = `${year}-12-31`

    setData(null)
    Promise.all([
      // 請求書は全件取得し、入金日（無ければ請求日）ベースで当月分・当年分にクライアント側で振り分ける
      supabase.from('invoices').select('*'),
      supabase.from('expenses').select('*').gte('expense_date', startOfMonth).lte('expense_date', endOfMonth),
      supabase.from('projects').select('*'),
      supabase.from('expenses').select('*').gte('expense_date', startOfYear).lte('expense_date', endOfYear),
      supabase.from('expenses').select('*').eq('is_recurring', true).eq('recurring_status', 'active'),
      supabase.from('wishlist').select('*').in('status', ['want', 'considering']),
      supabase.from('messages').select('cost_usd').eq('role', 'assistant').gte('created_at', startOfMonth).lte('created_at', endOfMonth + 'T23:59:59').not('cost_usd', 'is', null),
    ]).then(([invAll, expM, proj, expY, recur, wish, api]) => {
      const apiTotal = (api.data || []).reduce((s: number, m: { cost_usd: string }) => s + parseFloat(m.cost_usd || '0'), 0)
      const allInvoices: Invoice[] = invAll.data || []
      const monthInv = allInvoices.filter(i => { const d = revenueDate(i); return d >= startOfMonth && d <= endOfMonth })
      const yearInv = allInvoices.filter(i => { const d = revenueDate(i); return d >= startOfYear && d <= endOfYear })
      setData({
        invoices: monthInv,
        expenses: (expM.data || []).filter(e => e.is_deductible !== false),
        projects: proj.data || [],
        allInv: yearInv,
        allExp: (expY.data || []).filter(e => e.is_deductible !== false),
        recurSubs: recur.data || [],
        wishItems: wish.data || [],
        apiCostTotal: apiTotal,
      })
    })
  }, [viewDate])

  if (!data) return <div className="skeleton-card" style={{ height: 200 }} />

  const { invoices, expenses, projects, allInv, allExp, recurSubs, wishItems, apiCostTotal } = data
  const year = viewDate.getFullYear()
  const month = viewDate.getMonth() + 1
  const now = new Date()
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1

  const totalRev = invoices.reduce((s, i) => s + i.amount, 0)
  const totalExp = expenses.reduce((s, e) => s + e.amount, 0)
  const grossProfit = totalRev - totalExp
  const yearRev = allInv.reduce((s, i) => s + i.amount, 0)
  const yearExp = allExp.reduce((s, e) => s + e.amount, 0)
  const monthlyFixed = recurSubs.filter(s => s.is_deductible !== false).reduce((sum, s) => {
    if (s.recurring_interval === 'yearly') return sum + Math.round(s.amount / 12)
    if (s.recurring_interval === 'quarterly') return sum + Math.round(s.amount / 3)
    return sum + s.amount
  }, 0)
  const wishTotal = wishItems.reduce((s, w) => s + w.amount, 0)

  const kpis = [
    { l: '売上', v: fmtYen(totalRev), c: 'var(--accent)', sub: `年計 ${fmtYen(yearRev)}` },
    { l: '経費', v: fmtYen(totalExp), c: 'var(--text3)', sub: `年計 ${fmtYen(yearExp)}` },
    { l: '粗利', v: fmtYen(grossProfit), c: grossProfit >= 0 ? '#22c55e' : '#ef4444', sub: `${totalRev > 0 ? Math.round(grossProfit / totalRev * 100) : 0}%` },
    { l: '固定費(経費分)', v: fmtYen(monthlyFixed), c: 'var(--amber)', sub: `年${fmtYen(monthlyFixed * 12)}` },
    { l: 'ほしい物', v: fmtYen(wishTotal), c: 'var(--blue)', sub: `${wishItems.length}件` },
    { l: 'APIコスト', v: `$${apiCostTotal.toFixed(2)}`, c: 'var(--amber)', sub: `${Math.round(apiCostTotal * 150)}円` },
  ]

  // Build project map for client-name resolution
  const projMap = new Map(projects.map(p => [p.id, p]))

  // Build stacked bar data: monthly revenue by client
  const clientNameOf = (inv: Invoice) => (inv.project_id && projMap.get(inv.project_id)?.client_name) || inv.client_name || '未割当'
  const clientSet = new Set<string>()
  allInv.forEach(inv => clientSet.add(clientNameOf(inv)))
  const clientKeys = Array.from(clientSet)

  const monthlyByClient: Record<number, { total: number; clients: Record<string, number> }> = {}
  for (let m = 1; m <= 12; m++) {
    monthlyByClient[m] = { total: 0, clients: {} }
    clientKeys.forEach(ck => { monthlyByClient[m].clients[ck] = 0 })
  }
  allInv.forEach(inv => {
    const mon = parseInt(revenueDate(inv).substring(5, 7))
    const name = clientNameOf(inv)
    monthlyByClient[mon].clients[name] += inv.amount
    monthlyByClient[mon].total += inv.amount
  })

  const maxMonthly = Math.max(...Object.values(monthlyByClient).map(d => d.total)) || 1
  const ovMonthSet = new Set(allInv.map(i => revenueDate(i).substring(0, 7)))
  const dataMonths = Math.max(ovMonthSet.size, 1)
  const avgRev = yearRev / dataMonths
  const fmtAxis = (v: number) => v >= 10000 ? `${Math.round(v / 10000)}万` : fmtYen(v)

  const navMonth = (dir: number) => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + dir, 1))

  return (
    <div>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navMonth(-1)}>◀</button>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, minWidth: 120 }}>{year}年{month}月{!isCurrentMonth && <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8, fontWeight: 400 }}>(過去)</span>}</h3>
        <button className="btn btn-ghost btn-sm" onClick={() => navMonth(1)} disabled={isCurrentMonth} style={{ opacity: isCurrentMonth ? 0.3 : 1 }}>▶</button>
        {!isCurrentMonth && <button className="btn btn-ghost btn-sm" onClick={() => setViewDate(new Date(now.getFullYear(), now.getMonth(), 1))}>今月へ</button>}
      </div>

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 24 }}>
        {kpis.map(d => (
          <div key={d.l} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{d.l}</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: d.c }}>{d.v}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{d.sub}</div>
          </div>
        ))}
      </div>

      {/* Monthly stacked chart by client */}
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)' }}>{year}年 月別売上推移（入金の前月に計上・クライアント別）</div>
          <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--text3)', flexWrap: 'wrap' }}>
            {clientKeys.map((ck, i) => (
              <span key={ck} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: PROJ_COLORS[i % PROJ_COLORS.length] }} />
                {ck}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {/* Y-axis */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 6, height: 160, fontSize: 9, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            <span>{fmtAxis(maxMonthly)}</span>
            <span>{fmtAxis(Math.round(maxMonthly / 2))}</span>
            <span>0</span>
          </div>
          {/* Bars */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 3, height: 160, borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', padding: '0 2px', position: 'relative' }}>
            <div style={{ position: 'absolute', bottom: `${avgRev / maxMonthly * 100}%`, left: 0, right: 0, borderTop: '1px dashed var(--text3)', opacity: 0.3, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: `calc(${avgRev / maxMonthly * 100}% + 2px)`, right: 2, fontSize: 8, color: 'var(--text3)', opacity: 0.5, pointerEvents: 'none' }}>平均</div>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(mi => {
              const md = monthlyByClient[mi]
              const isViewing = mi === month
              const isFutureRel = year === now.getFullYear() && mi > now.getMonth() + 1
              const tipParts = [`${mi}月: 合計${fmtYen(md.total)}`]
              clientKeys.forEach(ck => { if (md.clients[ck]) tipParts.push(`${ck}: ${fmtYen(md.clients[ck])}`) })

              return (
                <div key={mi} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }} title={tipParts.join('\n')}
                  onClick={() => setViewDate(new Date(year, mi - 1, 1))}>
                  {md.total > 0 && !isFutureRel && (
                    <div style={{ fontSize: 8, color: 'var(--text3)', marginBottom: 1, whiteSpace: 'nowrap' }}>
                      {md.total >= 1000000 ? `${Math.round(md.total / 10000)}万` : `${Math.round(md.total / 1000)}K`}
                    </div>
                  )}
                  <div style={{ width: '80%', maxWidth: 28, display: 'flex', flexDirection: 'column-reverse', height: 150, opacity: isFutureRel ? 0.15 : 1, outline: isViewing ? '2px solid var(--accent)' : 'none', outlineOffset: 1, borderRadius: 2 }}>
                    {clientKeys.map((ck, ci) => {
                      const val = md.clients[ck] || 0
                      if (val <= 0) return null
                      const segH = val / maxMonthly * 150
                      return <div key={ck} style={{ width: '100%', height: segH, background: PROJ_COLORS[ci % PROJ_COLORS.length], minHeight: 2 }} />
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Month labels */}
        <div style={{ display: 'flex', marginLeft: 40, marginTop: 4 }}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(ml => (
            <div key={ml} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: ml === month ? 'var(--accent)' : 'var(--text3)', fontWeight: ml === month ? 600 : 400 }}>{ml}月</div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap', justifyContent: 'center' }}>
          <span>月平均売上: <span style={{ fontWeight: 600, color: 'var(--text2)' }}>{fmtYen(Math.round(avgRev))}</span></span>
          <span>年間予測: <span style={{ fontWeight: 600, color: 'var(--text2)' }}>{fmtYen(Math.round(avgRev * 12))}</span></span>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Invoices Tab
// ============================================================

function InvoiceForm({ existing, onSave, onClose, onDelete }: { existing: Invoice | null; onSave: () => void; onClose: () => void; onDelete: (id: string) => Promise<void> }) {
  const [clientName, setClientName] = useState(existing?.client_name || '')
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '')
  const [invoiceDate, setInvoiceDate] = useState(existing?.invoice_date || new Date().toISOString().substring(0, 10))
  const [dueDate, setDueDate] = useState(existing?.due_date || '')
  const [paidDate, setPaidDate] = useState(existing?.paid_date || '')
  const [status, setStatus] = useState(existing?.status || 'sent')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [fileUrl, setFileUrl] = useState(existing?.file_url || '')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const path = await uploadInvoiceFile(file)
      setFileUrl(path)
      toast('請求書ファイルをアップロードしました')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'アップロードに失敗しました')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const openFile = async () => {
    if (!fileUrl) return
    const url = await getInvoiceFileUrl(fileUrl)
    if (url) window.open(url, '_blank', 'noopener')
    else toast('ファイルを開けませんでした')
  }

  const save = async () => {
    if (!clientName.trim() || !amount) return
    setSaving(true)
    const payload: Partial<Invoice> = {
      client_name: clientName.trim(),
      amount: parseInt(amount),
      invoice_date: invoiceDate,
      due_date: dueDate || undefined,
      paid_date: paidDate || undefined,
      status,
      notes: notes.trim() || undefined,
      file_url: fileUrl || null,
    }
    const { error } = existing
      ? await supabase.from('invoices').update(payload).eq('id', existing.id)
      : await supabase.from('invoices').insert(payload)
    setSaving(false)
    if (error) { toast(`保存に失敗: ${error.message}`); return }
    toast(existing ? '更新しました' : '追加しました')
    onSave()
    onClose()
  }

  const remove = async () => {
    if (!existing || !confirm('この請求書を削除しますか？')) return
    setDeleting(true)
    await onDelete(existing.id)
    setDeleting(false)
  }

  return (
    <Modal open onClose={onClose} title={existing ? '請求書を編集' : '請求書を追加'}
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <button className="btn btn-primary" disabled={saving || !clientName.trim() || !amount} onClick={save}>{saving ? '保存中...' : existing ? '更新' : '追加'}</button>
          <button className="btn btn-ghost" onClick={onClose}>キャンセル</button>
          {existing && <button className="btn" style={{ color: 'var(--red)', marginLeft: 'auto' }} disabled={deleting} onClick={remove}>{deleting ? '削除中...' : '削除'}</button>}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          { label: '請求先', el: <input className="input" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="クライアント名" autoFocus /> },
          { label: '金額（税込）', el: <input className="input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" /> },
          { label: '請求日', el: <input className="input" type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} /> },
          { label: '支払期限', el: <input className="input" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /> },
          { label: '入金日', el: <input className="input" type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} /> },
          { label: 'ステータス', el: <select className="input" value={status} onChange={e => setStatus(e.target.value)}><option value="sent">送付済み</option><option value="paid">入金済み</option><option value="overdue">支払遅延</option><option value="draft">下書き</option><option value="cancelled">キャンセル</option></select> },
          { label: 'メモ', el: <textarea className="input" value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight: 60 }} placeholder="備考（任意）" /> },
        ].map(({ label, el }) => (
          <div key={label}>
            <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>{label}</label>
            {el}
          </div>
        ))}

        {/* 請求書ファイル（PDF） */}
        <div>
          <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>請求書ファイル（PDF）</label>
          {fileUrl && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
              <button type="button" className="btn btn-g btn-sm" onClick={openFile}>📄 ファイルを開く</button>
              <button type="button" className="btn btn-sm" style={{ color: 'var(--red)' }} onClick={() => setFileUrl('')}>添付を外す</button>
            </div>
          )}
          <input type="file" accept="application/pdf" disabled={uploading} onChange={handleFile} style={{ fontSize: 12 }} />
          {uploading && <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>アップロード中...</span>}
        </div>
      </div>
    </Modal>
  )
}

function FinInvoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState<Invoice | null>(null)

  const load = async () => {
    const res = await supabase.from('invoices').select('*').order('invoice_date', { ascending: false })
    setInvoices(res.data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('invoices').delete().eq('id', id)
    if (error) { toast(`削除に失敗: ${error.message}`); return }
    toast('削除しました')
    setShowForm(false); setEditItem(null); load()
  }

  if (loading) return <div className="skeleton-card" style={{ height: 150 }} />

  return (
    <div>
      <button className="btn btn-p btn-sm" style={{ marginBottom: 16 }} onClick={() => { setEditItem(null); setShowForm(true) }}>+ 請求書追加</button>

      {invoices.length === 0 ? (
        <div style={{ color: 'var(--text3)', padding: '40px 0', textAlign: 'center' }}>請求書が未登録です。「+ 請求書追加」から登録できます（PDF添付も可）。</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['日付', '請求先', '金額', '入金日', 'ステータス', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text3)', fontWeight: 500 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => { setEditItem(inv); setShowForm(true) }}>
                <td style={{ padding: '8px 12px' }}>{fmtDate(inv.invoice_date)}</td>
                <td style={{ padding: '8px 12px' }}>{inv.client_name}{inv.file_url && <span title="請求書ファイルあり" style={{ marginLeft: 6, fontSize: 11, color: 'var(--text3)' }}>📎</span>}</td>
                <td style={{ padding: '8px 12px', fontWeight: 600 }}>{fmtYen(inv.amount)}</td>
                <td style={{ padding: '8px 12px', color: 'var(--text3)' }}>{inv.paid_date ? fmtDate(inv.paid_date) : '-'}</td>
                <td style={{ padding: '8px 12px', color: 'var(--text3)', fontSize: 11 }}>{inv.status || '-'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                  <button className="btn btn-g btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={e => { e.stopPropagation(); setEditItem(inv); setShowForm(true) }}>編集</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && <InvoiceForm existing={editItem} onSave={load} onClose={() => { setShowForm(false); setEditItem(null) }} onDelete={handleDelete} />}
    </div>
  )
}

// ============================================================
// Expenses Tab
// ============================================================

const CAT_LABELS: Record<string, string> = {
  equipment: '機材', transportation: '交通費', communication: '通信費', office: '事務所',
  outsourcing: '外注', supplies: '消耗品', insurance: '保険', tax_payment: '税金',
  subscription: 'サブスク', education: '研修', entertainment: '交際費', other: '他'
}

function ExpenseForm({ onSave, onClose }: { onSave: () => void; onClose: () => void }) {
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().substring(0, 10))
  const [category, setCategory] = useState('other')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!description.trim() || !amount) return
    setSaving(true)
    const { error } = await supabase.from('expenses').insert({ description: description.trim(), amount: parseInt(amount), expense_date: expenseDate, category, is_deductible: true })
    setSaving(false)
    if (error) { toast(`保存に失敗: ${error.message}`); return }
    toast('追加しました')
    onSave()
    onClose()
  }

  return (
    <Modal open onClose={onClose} title="経費を追加"
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" disabled={saving || !description.trim() || !amount} onClick={save}>{saving ? '保存中...' : '追加'}</button>
          <button className="btn btn-ghost" onClick={onClose}>キャンセル</button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[
          { label: '内容', el: <input className="input" value={description} onChange={e => setDescription(e.target.value)} placeholder="経費の内容" autoFocus /> },
          { label: '金額（税込）', el: <input className="input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" /> },
          { label: '支払日', el: <input className="input" type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} /> },
          { label: 'カテゴリ', el: <select className="input" value={category} onChange={e => setCategory(e.target.value)}>{Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select> },
        ].map(({ label, el }) => (
          <div key={label}>
            <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>{label}</label>
            {el}
          </div>
        ))}
      </div>
    </Modal>
  )
}

function FinExpenses() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)

  const load = async () => {
    const res = await supabase.from('expenses').select('*').order('expense_date', { ascending: false })
    setExpenses(res.data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  if (loading) return <div className="skeleton-card" style={{ height: 150 }} />

  const monthTotal = expenses.reduce((s, e) => s + e.amount, 0)
  const byCat: Record<string, number> = {}
  expenses.forEach(e => { byCat[e.category] = (byCat[e.category] || 0) + e.amount })

  return (
    <div>
      <button className="btn btn-p btn-sm" style={{ marginBottom: 16 }} onClick={() => setShowForm(true)}>+ 経費追加</button>

      {expenses.length === 0 ? (
        <div style={{ color: 'var(--text3)', padding: '40px 0', textAlign: 'center' }}>経費が未登録です。/invoice expense で追加してください。</div>
      ) : (
        <>
          <div style={{ marginBottom: 20, fontSize: 14 }}>
            <span style={{ color: 'var(--text3)' }}>合計: </span>
            <span style={{ fontWeight: 600, fontSize: 18 }}>{fmtYen(monthTotal)}</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            {Object.keys(byCat).sort((a, b) => byCat[b] - byCat[a]).map(cat => (
              <div key={cat} style={{ fontSize: 12, color: 'var(--text3)' }}>
                {CAT_LABELS[cat] || cat} <span style={{ fontWeight: 500, color: 'var(--text)' }}>{fmtYen(byCat[cat])} ({monthTotal > 0 ? Math.round(byCat[cat] / monthTotal * 100) : 0}%)</span>
              </div>
            ))}
          </div>
          {expenses.map(e => (
            <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <div>
                <span>{e.description}</span>
                <span style={{ color: 'var(--text3)' }}> · {CAT_LABELS[e.category] || e.category}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ color: 'var(--text3)', fontSize: 12 }}>{fmtDate(e.expense_date)}</span>
                <span style={{ fontWeight: 600 }}>{fmtYen(e.amount)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {showForm && <ExpenseForm onSave={load} onClose={() => setShowForm(false)} />}
    </div>
  )
}

// ============================================================
// Subscriptions Tab
// ============================================================

function FinSubscriptions() {
  const [subs, setSubs] = useState<Expense[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState<Expense | null>(null)

  const load = async () => {
    const res = await supabase.from('expenses').select('*').eq('is_recurring', true).order('recurring_status').order('amount', { ascending: false })
    setSubs(res.data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  if (loading) return <div className="skeleton-card" style={{ height: 150 }} />

  const intervalLabels: Record<string, string> = { monthly: '月額', quarterly: '四半期', yearly: '年額' }
  const statusColors: Record<string, string> = { active: 'var(--green)', paused: 'var(--amber)', cancelled: 'var(--text3)' }
  const statusLabels: Record<string, string> = { active: '契約中', paused: '一時停止', cancelled: '解約済み' }
  const catColors: Record<string, string> = { subscription: '#5046e5', communication: '#2563eb', equipment: '#0d9f6e', office: '#d97706', outsourcing: '#dc2626', education: '#8b5cf6', insurance: '#06b6d4', other: '#6b7280', supplies: '#f59e0b', entertainment: '#ec4899', tax_payment: '#64748b', transportation: '#14b8a6' }

  const active = subs.filter(s => s.recurring_status === 'active')
  const toMonthly = (s: Expense) => s.recurring_interval === 'yearly' ? Math.round(s.amount / 12) : s.recurring_interval === 'quarterly' ? Math.round(s.amount / 3) : s.amount
  const monthlyTotal = active.reduce((sum, s) => sum + toMonthly(s), 0)
  const monthlyDeductible = active.filter(s => s.is_deductible !== false).reduce((sum, s) => sum + toMonthly(s), 0)
  const monthlyPrivate = monthlyTotal - monthlyDeductible

  const getMonthly = (s: Expense) => s.recurring_interval === 'yearly' ? Math.round(s.amount / 12) : s.recurring_interval === 'quarterly' ? Math.round(s.amount / 3) : s.amount
  const maxMonthly = Math.max(...subs.filter(s => s.recurring_status === 'active').map(getMonthly), 1)

  const saveForm = async (payload: Partial<Expense>) => {
    if (editItem) {
      await supabase.from('expenses').update(payload).eq('id', editItem.id)
    } else {
      await supabase.from('expenses').insert({ ...payload, expense_date: new Date().toISOString().substring(0, 10), is_recurring: true })
    }
    setShowForm(false); setEditItem(null); load()
  }

  return (
    <div>
      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 24 }}>
        {[{ l: '月額合計', v: fmtYen(monthlyTotal), c: 'var(--accent)' }, { l: '内 経費', v: fmtYen(monthlyDeductible), c: 'var(--green)' }, { l: '内 プライベート', v: fmtYen(monthlyPrivate), c: 'var(--text3)' }, { l: '年額合計', v: fmtYen(monthlyTotal * 12), c: 'var(--text)' }, { l: '契約数', v: `${active.length}件`, c: 'var(--text2)' }].map(d => (
          <div key={d.l} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{d.l}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: d.c }}>{d.v}</div>
          </div>
        ))}
      </div>

      <button className="btn btn-p btn-sm" style={{ marginBottom: 16 }} onClick={() => { setEditItem(null); setShowForm(true) }}>+ 固定費を追加</button>

      {subs.length === 0 ? (
        <div style={{ color: 'var(--text3)', padding: '40px 0', textAlign: 'center' }}>固定費が未登録です。</div>
      ) : subs.map(s => {
        const monthlyAmt = getMonthly(s)
        const barPct = Math.round(monthlyAmt / maxMonthly * 100)
        const isCancelled = s.recurring_status === 'cancelled'
        return (
          <div key={s.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13, opacity: isCancelled ? 0.35 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[s.recurring_status || ''] || 'var(--text3)', flexShrink: 0 }} />
                <span style={{ fontWeight: 500 }}>{s.service_name || s.description}</span>
                <span style={{ fontSize: 10, color: 'var(--text3)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}>{CAT_LABELS[s.category] || s.category}</span>
                {s.is_deductible === false && <span style={{ fontSize: 10, color: 'var(--text3)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3, border: '1px dashed var(--border)' }}>プライベート</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontFamily: 'var(--mono)', fontSize: 14 }}>{fmtYen(monthlyAmt)}/月</span>
                <button className="btn btn-g btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => { setEditItem(s); setShowForm(true) }}>編集</button>
              </div>
            </div>
            {!isCancelled && (
              <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${barPct}%`, background: catColors[s.category] || '#6b7280', borderRadius: 3 }} />
              </div>
            )}
            {s.recurring_interval !== 'monthly' && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{intervalLabels[s.recurring_interval || ''] || ''} {fmtYen(s.amount)}</div>}
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{statusLabels[s.recurring_status || ''] || ''}</div>
          </div>
        )
      })}

      {showForm && (
        <SubsForm
          existing={editItem}
          onSave={saveForm}
          onClose={() => { setShowForm(false); setEditItem(null) }}
          onDelete={async (id) => { await supabase.from('expenses').delete().eq('id', id); setShowForm(false); setEditItem(null); load() }}
        />
      )}
    </div>
  )
}

function SubsForm({ existing, onSave, onClose, onDelete }: { existing: Expense | null; onSave: (p: Partial<Expense>) => void; onClose: () => void; onDelete: (id: string) => void }) {
  const [name, setName] = useState(existing?.service_name || existing?.description || '')
  const [amount, setAmount] = useState(String(existing?.amount || ''))
  const [interval, setInterval] = useState(existing?.recurring_interval || 'monthly')
  const [category, setCategory] = useState(existing?.category || 'subscription')
  const [status, setStatus] = useState(existing?.recurring_status || 'active')
  const [isDeductible, setIsDeductible] = useState(existing?.is_deductible !== false)

  const save = () => {
    if (!name || !amount) return
    onSave({ service_name: name, description: name, amount: parseInt(amount), category, is_recurring: true, recurring_interval: interval, recurring_status: status, is_deductible: isDeductible })
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)' }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{existing ? '固定費を編集' : '固定費を追加'}</div>
        {[
          { label: 'サービス名', el: <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="ChatGPT Plus" /> },
          { label: '金額（税込）', el: <input className="input" type="number" value={amount} onChange={e => setAmount(e.target.value)} /> },
          { label: '支払い間隔', el: <select className="input" value={interval} onChange={e => setInterval(e.target.value)}><option value="monthly">月額</option><option value="quarterly">四半期</option><option value="yearly">年額</option></select> },
          { label: 'カテゴリ', el: <select className="input" value={category} onChange={e => setCategory(e.target.value)}>{Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select> },
          { label: 'ステータス', el: <select className="input" value={status} onChange={e => setStatus(e.target.value)}><option value="active">契約中</option><option value="paused">一時停止</option><option value="cancelled">解約済み</option></select> },
          { label: '経費として計上', el: <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 0' }}><input type="checkbox" checked={isDeductible} onChange={e => setIsDeductible(e.target.checked)} />{isDeductible ? '経費（確定申告で控除）' : 'プライベート支出'}</label> },
        ].map(({ label, el }) => (
          <div key={label} style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2, display: 'block' }}>{label}</label>
            {el}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-p" onClick={save}>{existing ? '更新' : '追加'}</button>
          <button className="btn btn-g" onClick={onClose}>キャンセル</button>
          {existing && <button className="btn" style={{ color: 'var(--red)', marginLeft: 'auto' }} onClick={() => { if (confirm('削除しますか？')) onDelete(existing.id) }}>削除</button>}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Projects Tab
// ============================================================

function FinProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('projects').select('*').order('status').order('created_at').then(({ data }) => { setProjects(data || []); setLoading(false) })
  }, [])

  if (loading) return <div className="skeleton-card" style={{ height: 150 }} />
  if (projects.length === 0) return <div style={{ color: 'var(--text3)', padding: '40px 0', textAlign: 'center' }}>案件が未登録です。/invoice project add で追加してください。</div>

  const statusLabels: Record<string, string> = { active: '稼働中', completed: '完了', paused: '中断', cancelled: '中止' }
  const typeLabels: Record<string, string> = { project: 'プロジェクト', monthly: '月額', hourly: '時間制', consulting: 'コンサル' }

  return (
    <div>
      {projects.map(p => {
        const statusColor = p.status === 'active' ? '#22c55e' : p.status === 'completed' ? 'var(--text3)' : '#f59e0b'
        return (
          <div key={p.id} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {p.name}<span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {p.client_name}</span>
              </div>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: `${statusColor}20`, color: statusColor }}>{statusLabels[p.status] || p.status}</span>
            </div>
            <div style={{ display: 'flex', gap: 24, fontSize: 12, color: 'var(--text3)' }}>
              <span>{typeLabels[p.contract_type] || p.contract_type}</span>
              {p.default_rate && <span>目標: {fmtYen(p.default_rate)}/h</span>}
              {p.budget && <span>予算: {fmtYen(p.budget)}</span>}
              {p.start_date && <span>{fmtDate(p.start_date)}〜{p.end_date ? fmtDate(p.end_date) : ''}</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ============================================================
// Wishlist Tab
// ============================================================

function FinWishlist() {
  const [items, setItems] = useState<WishlistItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState<WishlistItem | null>(null)

  const load = async () => {
    const res = await supabase.from('wishlist').select('*').order('priority').order('created_at', { ascending: false })
    setItems(res.data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  if (loading) return <div className="skeleton-card" style={{ height: 150 }} />

  const statusLabels: Record<string, string> = { want: '欲しい', considering: '検討中', purchased: '購入済', dropped: '見送り' }
  const statusColors: Record<string, string> = { want: 'var(--accent)', considering: 'var(--amber)', purchased: 'var(--green)', dropped: 'var(--text3)' }
  const catLabels: Record<string, string> = { equipment: '機材', software: 'ソフトウェア', furniture: '家具', experience: '体験', book: '本', gadget: 'ガジェット', other: 'その他' }
  const active = items.filter(i => i.status === 'want' || i.status === 'considering')
  const totalWant = active.reduce((s, i) => s + i.amount, 0)
  const maxAmt = items.length > 0 ? Math.max(...items.map(i => i.amount), 1) : 1

  const saveWish = async (payload: Partial<WishlistItem>) => {
    if (editItem) {
      if (payload.status === 'purchased' && editItem.status !== 'purchased') payload.purchased_at = new Date().toISOString()
      await supabase.from('wishlist').update(payload).eq('id', editItem.id)
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { toast('ログインが必要です'); return }
      await supabase.from('wishlist').insert({ ...payload, owner_id: user.id })
    }
    setShowForm(false); setEditItem(null); load()
  }

  const markPurchased = async (item: WishlistItem) => {
    await supabase.from('wishlist').update({ status: 'purchased', purchased_at: new Date().toISOString() }).eq('id', item.id)
    toast(`「${item.title}」を購入済みに（履歴に保存）`)
    load()
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
        {[{ l: 'ほしい物合計', v: fmtYen(totalWant), c: 'var(--accent)' }, { l: 'アイテム数', v: `${active.length}件`, c: 'var(--text)' }, { l: '購入済み', v: `${items.filter(i => i.status === 'purchased').length}件`, c: 'var(--green)' }].map(d => (
          <div key={d.l} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>{d.l}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: d.c }}>{d.v}</div>
          </div>
        ))}
      </div>
      <button className="btn btn-p btn-sm" style={{ marginBottom: 16 }} onClick={() => { setEditItem(null); setShowForm(true) }}>+ ほしい物を追加</button>
      {items.length === 0 && <div style={{ color: 'var(--text3)', padding: '40px 0', textAlign: 'center' }}>ほしい物リストが空です。</div>}
      {items.map(item => {
        const isDropped = item.status === 'dropped'
        const isPurchased = item.status === 'purchased'
        const barPct = Math.round(item.amount / maxAmt * 100)
        return (
          <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border)', opacity: isDropped ? 0.35 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColors[item.status] || 'var(--text3)', flexShrink: 0 }} />
                <span style={{ fontWeight: 500, fontSize: 13, textDecoration: isPurchased ? 'line-through' : undefined, color: isPurchased ? 'var(--text3)' : undefined }}>{item.title}</span>
                {item.category && item.category !== 'other' && <span style={{ fontSize: 10, color: 'var(--text3)', background: 'var(--surface2)', padding: '1px 6px', borderRadius: 3 }}>{catLabels[item.category] || item.category}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontWeight: 700, fontFamily: 'var(--mono)', fontSize: 15, color: item.amount >= 100000 ? 'var(--red)' : item.amount >= 30000 ? 'var(--amber)' : 'var(--text)' }}>{fmtYen(item.amount)}</span>
                {!isPurchased && !isDropped && (
                  <button className="btn btn-p btn-sm" style={{ fontSize: 10, padding: '2px 10px' }} title="購入済みにして履歴に保存（リストから消える）" onClick={() => markPurchased(item)}>✓ 購入した</button>
                )}
                <button className="btn btn-g btn-sm" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => { setEditItem(item); setShowForm(true) }}>編集</button>
              </div>
            </div>
            {!isDropped && !isPurchased && (
              <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden', marginBottom: 2 }}>
                <div style={{ height: '100%', width: `${barPct}%`, background: statusColors[item.status] || 'var(--accent)', borderRadius: 3 }} />
              </div>
            )}
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>{statusLabels[item.status] || item.status}{item.url ? ' · リンクあり' : ''}</div>
          </div>
        )
      })}
      {showForm && (
        <WishForm existing={editItem} onSave={saveWish} onClose={() => { setShowForm(false); setEditItem(null) }} onDelete={async (id) => { await supabase.from('wishlist').delete().eq('id', id); setShowForm(false); setEditItem(null); load() }} catLabels={catLabels} />
      )}
    </div>
  )
}

function WishForm({ existing, onSave, onClose, onDelete, catLabels }: { existing: WishlistItem | null; onSave: (p: Partial<WishlistItem>) => void; onClose: () => void; onDelete: (id: string) => void; catLabels: Record<string, string> }) {
  const [name, setName] = useState(existing?.title || '')
  const [amount, setAmount] = useState(String(existing?.amount || ''))
  const [url, setUrl] = useState(existing?.url || '')
  const [desc, setDesc] = useState(existing?.description || '')
  const [category, setCategory] = useState(existing?.category || 'other')
  const [priority, setPriority] = useState(existing?.priority || 'normal')
  const [status, setStatus] = useState(existing?.status || 'want')

  const save = () => {
    if (!name) return
    const payload: Partial<WishlistItem> = { title: name, amount: parseInt(amount) || 0, url: url.trim() || undefined, description: desc.trim() || undefined, category, priority }
    if (existing) payload.status = status
    onSave(payload)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)' }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{existing ? 'ほしい物を編集' : 'ほしい物を追加'}</div>
        {[
          { label: 'アイテム名', el: <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="何がほしい？" /> },
          { label: '金額（税込）', el: <input className="input" type="number" value={amount} onChange={e => setAmount(e.target.value)} /> },
          { label: 'URL', el: <input className="input" value={url} onChange={e => setUrl(e.target.value)} placeholder="商品URL（任意）" /> },
          { label: 'メモ', el: <textarea className="input" value={desc} onChange={e => setDesc(e.target.value)} style={{ minHeight: 40 }} /> },
          { label: 'カテゴリ', el: <select className="input" value={category} onChange={e => setCategory(e.target.value)}>{Object.entries(catLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select> },
          { label: '優先度', el: <select className="input" value={priority} onChange={e => setPriority(e.target.value)}><option value="high">高</option><option value="normal">中</option><option value="low">低</option></select> },
          ...(existing ? [{ label: 'ステータス', el: <select className="input" value={status} onChange={e => setStatus(e.target.value)}><option value="want">欲しい</option><option value="considering">検討中</option><option value="purchased">購入済み</option><option value="dropped">見送り</option></select> }] : []),
        ].map(({ label, el }) => (
          <div key={label} style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2, display: 'block' }}>{label}</label>
            {el}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn btn-p" onClick={save}>{existing ? '更新' : '追加'}</button>
          <button className="btn btn-g" onClick={onClose}>キャンセル</button>
          {existing && <button className="btn" style={{ color: 'var(--red)', marginLeft: 'auto' }} onClick={() => { if (confirm('削除しますか？')) onDelete(existing.id) }}>削除</button>}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tax Tab
// ============================================================

function FinTax() {
  const year = new Date().getFullYear()
  const [projectedRevenue, setProjectedRevenue] = useState(0)
  const [actualRevQ, setActualRevQ] = useState(0)
  const [monthsWithData, setMonthsWithData] = useState(1)
  const [payments, setPayments] = useState<TaxPayment[]>([])
  const [simRev, setSimRev] = useState(0)
  const [simExp, setSimExp] = useState(0)
  const [simSalary, setSimSalary] = useState(() => Number(localStorage.getItem('finTax.simSalary') || 0))
  const [simSocial, setSimSocial] = useState(() => Number(localStorage.getItem('finTax.simSocial') || 0))
  const [simWithheld, setSimWithheld] = useState(() => Number(localStorage.getItem('finTax.simWithheld') || 0))
  const [simBusinessWithheld, setSimBusinessWithheld] = useState(() => Number(localStorage.getItem('finTax.simBusinessWithheld') || 0))
  const [reservedSoFar, setReservedSoFar] = useState(() => Number(localStorage.getItem('finTax.reservedSoFar') || 0))
  const [taxSavingState, setTaxSavingState] = useState<Record<string, TSEntry>>(loadTaxSavingState)
  const [blueDeduction, setBlueDeduction] = useState(() => {
    const stored = localStorage.getItem('finTax.blueDeduction')
    return stored === null ? 650_000 : Number(stored)
  })
  const [annualizationMonths, setAnnualizationMonths] = useState(() => Number(localStorage.getItem('finTax.annualizationMonths') || 12))
  const [isBizTaxable, setIsBizTaxable] = useState(() => localStorage.getItem('finTax.isBizTaxable') === 'true')
  const [showDetail, setShowDetail] = useState(false)
  const [showAssumptions, setShowAssumptions] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => { localStorage.setItem('finTax.simSalary', String(simSalary)) }, [simSalary])
  useEffect(() => { localStorage.setItem('finTax.simSocial', String(simSocial)) }, [simSocial])
  useEffect(() => { localStorage.setItem('finTax.simWithheld', String(simWithheld)) }, [simWithheld])
  useEffect(() => { localStorage.setItem('finTax.simBusinessWithheld', String(simBusinessWithheld)) }, [simBusinessWithheld])
  useEffect(() => { localStorage.setItem('finTax.reservedSoFar', String(reservedSoFar)) }, [reservedSoFar])
  useEffect(() => { localStorage.setItem(TS_KEY, JSON.stringify(taxSavingState)) }, [taxSavingState])
  useEffect(() => { localStorage.setItem('finTax.blueDeduction', String(blueDeduction)) }, [blueDeduction])
  useEffect(() => { localStorage.setItem('finTax.annualizationMonths', String(annualizationMonths)) }, [annualizationMonths])
  useEffect(() => { localStorage.setItem('finTax.isBizTaxable', String(isBizTaxable)) }, [isBizTaxable])

  useEffect(() => {
    Promise.all([
      // 売上は入金日（無ければ請求日）ベースで当年分を抽出する
      supabase.from('invoices').select('amount,invoice_date,paid_date'),
      supabase.from('expenses').select('amount,expense_date,is_deductible').gte('expense_date', `${year}-01-01`).lte('expense_date', `${year}-12-31`),
      supabase.from('tax_payments').select('*').gte('due_date', `${year}-01-01`).lte('due_date', `${year}-12-31`).order('due_date'),
    ]).then(([invRes, expRes, taxRes]) => {
      const yStart = `${year}-01-01`, yEnd = `${year}-12-31`
      const invoices = (invRes.data || []).filter((i: { invoice_date: string; paid_date?: string | null }) => { const d = revenueDate(i); return d >= yStart && d <= yEnd })
      const rev = invoices.reduce((s: number, i: { amount: number }) => s + i.amount, 0)
      setActualRevQ(rev)
      const invMonthSet: Record<string, boolean> = {}
      invoices.forEach((i: { invoice_date: string; paid_date?: string | null }) => { invMonthSet[revenueDate(i).substring(0, 7)] = true })
      const mwd = Math.max(Object.keys(invMonthSet).length, 1)
      setMonthsWithData(mwd)
      const projected = Math.round(rev / mwd * annualizationMonths)
      setProjectedRevenue(projected)
      setSimRev(projected)
      const expenses = (expRes.data || []).filter((e: { is_deductible?: boolean }) => e.is_deductible !== false)
      const expMonthSet = new Set(expenses.map((e: { expense_date: string }) => e.expense_date.substring(0, 7)))
      const expenseMonths = Math.max(expMonthSet.size, 1)
      const expenseTotal = expenses.reduce((sum: number, e: { amount: number }) => sum + e.amount, 0)
      setSimExp(Math.round(expenseTotal / expenseMonths * annualizationMonths))
      const taxPayments = (taxRes.data || []) as TaxPayment[]
      setPayments(taxPayments)
      if (Number(localStorage.getItem('finTax.simSocial') || 0) === 0) {
        const socialTotal = taxPayments
          .filter(p => p.tax_type === 'health_insurance' || p.tax_type === 'national_pension')
          .reduce((sum, p) => sum + p.amount, 0)
        if (socialTotal > 0) setSimSocial(socialTotal)
      }
      setLoaded(true)
    })
  }, [annualizationMonths, year])

  const getSavingEntry = (id: string): TSEntry => taxSavingState[id] || emptyTSEntry()
  const setSavingEntry = (id: string, patch: Partial<TSEntry>) => {
    setTaxSavingState(current => ({
      ...current,
      [id]: { ...(current[id] || emptyTSEntry()), ...patch },
    }))
  }
  const updateSavingMonthly = (id: 'kyosai' | 'ideco', monthlyMax: number, monthly: number) => {
    const normalizedMonthly = Math.min(monthlyMax, Math.max(0, monthly))
    setSavingEntry(id, {
      monthly: normalizedMonthly,
      status: normalizedMonthly > 0 ? 'active' : 'none',
    })
  }
  const kyosaiEntry = getSavingEntry('kyosai')
  const idecoEntry = getSavingEntry('ideco')
  const simKyosai = kyosaiEntry.status === 'active'
    ? annualContribution(kyosaiEntry.monthly, kyosaiEntry.startMonth, 840_000)
    : 0
  const simIdeco = idecoEntry.status === 'active'
    ? annualContribution(idecoEntry.monthly, idecoEntry.startMonth, 816_000)
    : 0

  if (!loaded) return <div className="skeleton-card" style={{ height: 200 }} />

  const t = calcTax({
    revenue: simRev,
    expense: simExp,
    salaryIncome: simSalary,
    socialInsurance: simSocial,
    isBusinessTaxable: isBizTaxable,
    withheldTax: simWithheld,
    businessWithheld: simBusinessWithheld,
    blueDeduction,
    mutualAidDeduction: simKyosai + simIdeco,
  })
  const now = new Date()
  const ny = year + 1
  const calcSchedule: { label: string; sub: string; due: string; amount: number; type: string }[] = [
    { label: '所得税 確定申告', sub: `${year}年度分${simWithheld + simBusinessWithheld > 0 ? '（源泉徴収済差引後）' : ''}`, due: `${ny}-03-15`, amount: t.additionalIncomeTax, type: 'income_tax' },
    { label: '住民税 1期', sub: `${year}年度分`, due: `${ny}-06-30`, amount: Math.ceil(t.residentTax / 4), type: 'resident_tax' },
    { label: '個人事業税 1期', sub: `${year}年度分`, due: `${ny}-08-31`, amount: Math.ceil(t.businessTax / 2), type: 'business_tax' },
    { label: '住民税 2期', sub: `${year}年度分`, due: `${ny}-08-31`, amount: Math.ceil(t.residentTax / 4), type: 'resident_tax' },
    { label: '住民税 3期', sub: `${year}年度分`, due: `${ny}-10-31`, amount: Math.ceil(t.residentTax / 4), type: 'resident_tax' },
    { label: '個人事業税 2期', sub: `${year}年度分`, due: `${ny}-11-30`, amount: Math.floor(t.businessTax / 2), type: 'business_tax' },
    { label: '住民税 4期', sub: `${year}年度分`, due: `${ny + 1}-01-31`, amount: Math.floor(t.residentTax / 4), type: 'resident_tax' },
  ]
  const dbSchedule = payments
    .filter(p => p.tax_type === 'health_insurance' || p.tax_type === 'national_pension')
    .map(p => {
      const m = new Date(p.due_date).getMonth() + 1
      const isHealth = p.tax_type === 'health_insurance'
      return {
        label: isHealth ? '任意継続健保' : '国民年金',
        sub: isHealth ? `${m}月分` : `${year}年度`,
        due: p.due_date,
        amount: p.amount,
        type: p.tax_type,
      }
    })
  const schedule = [...calcSchedule, ...dbSchedule]
    .filter(s => s.amount > 0)
    .sort((a, b) => a.due.localeCompare(b.due))

  const paidMap: Record<string, TaxPayment> = {}
  payments.forEach(p => { if (p.status === 'paid') paidMap[`${p.tax_type}_${p.due_date}`] = p })

  const schedTotal = schedule.reduce((s, p) => s + p.amount, 0)
  const reservePlan = calcReservePlan(
    schedule
      .filter(p => !paidMap[`${p.type}_${p.due}`])
      .map(p => ({ amount: p.amount, due: p.due })),
    reservedSoFar,
    now,
  )
  let schedPaid = 0

  const typeColors: Record<string, string> = {
    income_tax: '#ef4444', resident_tax: '#f59e0b', business_tax: '#8b5cf6', estimated_tax: '#3b82f6',
    health_insurance: '#06b6d4', national_pension: '#14b8a6'
  }
  const effRate = t.revenue > 0 ? (schedTotal / t.revenue * 100).toFixed(1) : '0'

  return (
    <div>
      {/* Simulation */}
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px' }}>税金シミュレーション</h3>

      <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: 'var(--text3)', cursor: 'pointer', marginBottom: 16 }} onClick={() => setShowAssumptions(v => !v)}>
        計算の前提条件 {showAssumptions ? '▾' : '▸'}
      </button>
      {showAssumptions && (
        <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 12, lineHeight: 1.8, color: 'var(--text2)' }}>
          {[
            ['申告区分', '個人事業主・青色申告（e-Tax）'],
            ['青色申告特別控除', `${Math.round(blueDeduction / 10_000)}万円（詳細条件で変更可能）`],
            ['基礎控除', '合計所得に応じて58万〜95万円（令和7・8年分）'],
            ['給与所得控除', '給与収入帯ごとに自動計算（65万〜195万）'],
            ['社会保険料控除', '入力された国保 + 国民年金 + 厚生年金等を全額控除'],
            ['小規模企業共済等掛金控除', '小規模企業共済・iDeCoの掛金全額（所得税・住民税とも）'],
            ['所得税率', '累進課税 5%〜45%'],
            ['住民税', '基礎控除43万円で別計算した課税所得 × 10% + 均等割・森林環境税 5,000円'],
            ['個人事業税', 'プログラマー/SE（自由職業）なら法定70業種に該当せず非課税。請負業/コンサル業認定時のみトグルON →（事業所得 - 290万円）× 5%'],
            ['年間売上の予測', `入金実績 ${monthsWithData}ヶ月分から${annualizationMonths}ヶ月に按分（${fmtYen(actualRevQ)} ÷ ${monthsWithData} × ${annualizationMonths}）`],
            ['注意', 'あくまで概算です。正確な税額は税理士にご確認ください'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 8, padding: '2px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ minWidth: 130, color: 'var(--text3)', flexShrink: 0, fontWeight: 500 }}>{k}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Input controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--text2)', minWidth: 120 }}>年間売上（税抜）</label>
        <input type="number" value={simRev} onChange={e => setSimRev(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--bg2)', color: 'var(--text)' }} />
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>({fmtYen(projectedRevenue)} 予測)</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--text2)', minWidth: 120 }}>年換算の対象月数</label>
        <input type="number" min={1} max={12} value={annualizationMonths} onChange={e => setAnnualizationMonths(Math.min(12, Math.max(1, parseInt(e.target.value) || 12)))} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--bg2)', color: 'var(--text)' }} />
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>開業月に応じて変更（通常12、2月開業なら11）</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <label style={{ fontSize: 13, color: 'var(--text2)', minWidth: 120 }}>年間経費</label>
        <input type="number" value={simExp} onChange={e => setSimExp(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--bg2)', color: 'var(--text)' }} />
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtYen(simExp)}</span>
      </div>
      <div style={{ paddingLeft: 132, marginBottom: 4 }}>
        <input type="range" min={0} max={Math.max(simRev, 5000000)} step={10000} value={simExp} onChange={e => setSimExp(parseInt(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)', width: '100%' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, paddingLeft: 132, flexWrap: 'wrap' }}>
        {[0, 500000, 1000000, 2000000, 3000000, 5000000].map(v => (
          <button key={v} style={{ padding: '4px 10px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg2)', color: 'var(--text2)', fontSize: 11, cursor: 'pointer' }} onClick={() => setSimExp(v)}>
            {v === 0 ? '¥0' : `¥${Math.round(v / 10000)}万`}
          </button>
        ))}
      </div>

      {/* 詳細入力（給与・社会保険料・控除・事業税） */}
      <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: 'var(--text3)', cursor: 'pointer', marginBottom: 12 }} onClick={() => setShowAdvanced(v => !v)}>
        詳細条件（給与/社会保険料/控除/事業税） {showAdvanced ? '▾' : '▸'}
      </button>
      {showAdvanced && (
        <div style={{ marginBottom: 20, padding: 16, borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>給与収入（事業外・年額）</label>
            <input type="number" value={simSalary} onChange={e => setSimSalary(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>正社員時代の給与額面（1月分など）</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>給与の源泉徴収済所得税</label>
            <input type="number" value={simWithheld} onChange={e => setSimWithheld(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>確定申告で精算（追加納付額から差引）</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>報酬の源泉徴収（事業・年額）</label>
            <input type="number" value={simBusinessWithheld} onChange={e => setSimBusinessWithheld(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>報酬から源泉徴収済みの所得税（追加納付額から差引）</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>社会保険料控除（年額）</label>
            <input type="number" value={simSocial} onChange={e => setSimSocial(parseInt(e.target.value) || 0)} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>国保 + 国民年金 + 厚生年金等の合算</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>小規模企業共済（月額）</label>
            <input type="number" min={0} max={70_000} value={kyosaiEntry.monthly} onChange={e => updateSavingMonthly('kyosai', 70_000, parseInt(e.target.value) || 0)} style={{ width: 120, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <select value={kyosaiEntry.startMonth ?? 1} onChange={e => setSavingEntry('kyosai', { startMonth: Number(e.target.value) })} style={{ width: 90, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={month}>{month}月開始</option>)}
            </select>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>当年控除 {fmtYen(simKyosai)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>iDeCo（月額）</label>
            <input type="number" min={0} max={68_000} value={idecoEntry.monthly} onChange={e => updateSavingMonthly('ideco', 68_000, parseInt(e.target.value) || 0)} style={{ width: 120, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }} />
            <select value={idecoEntry.startMonth ?? 1} onChange={e => setSavingEntry('ideco', { startMonth: Number(e.target.value) })} style={{ width: 90, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={month}>{month}月開始</option>)}
            </select>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>当年控除 {fmtYen(simIdeco)}</span>
          </div>
          <div style={{ paddingLeft: 212, margin: '-2px 0 10px', fontSize: 11, color: 'var(--text3)' }}>節税タブの加入設定と連動</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>青色申告特別控除</label>
            <select value={blueDeduction} onChange={e => setBlueDeduction(Number(e.target.value))} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--surface2)', color: 'var(--text)' }}>
              <option value={650_000}>65万円</option>
              <option value={100_000}>10万円</option>
              <option value={0}>控除なし</option>
            </select>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>申告方法・承認状況に合わせて選択</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 200 }}>個人事業税の課税</label>
            <button onClick={() => setIsBizTaxable(v => !v)} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border)', background: isBizTaxable ? '#8b5cf6' : 'var(--surface2)', color: isBizTaxable ? '#fff' : 'var(--text2)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
              {isBizTaxable ? '課税ON（請負業/コンサル業）' : '不課税（プログラマー/SE）'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>時給制・実働ベースなら不課税で申告可</span>
          </div>
        </div>
      )}

      {/* Reserve card */}
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>月次税金積立ガイド</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{year}年度 想定売上 {fmtYen(simRev)} / 経費 {fmtYen(simExp)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>積立済み額</label>
          <input type="number" min={0} value={reservedSoFar} onChange={e => setReservedSoFar(Math.max(0, parseInt(e.target.value) || 0))} style={{ width: 160, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--font)', background: 'var(--bg2)', color: 'var(--text)' }} />
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>納期の早い順に充当</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 16, marginBottom: 20 }}>
          {[
            { l: '毎月の積立目安', v: `${fmtYen(reservePlan.monthlyTotal)}/月`, c: '#f59e0b', sub: '各納期までの月数で按分' },
            { l: '現金確保すべき総額', v: fmtYen(reservePlan.uncovered), c: '#3b82f6', sub: reservedSoFar > 0 ? `積立済み ${fmtYen(reservedSoFar)} 充当後` : '社保＋税金（未払い分）' },
            {
              l: '事業手取り目安',
              v: fmtYen(Math.floor(t.takeHome / 12)) + '/月',
              c: '#22c55e',
              sub: simKyosai + simIdeco > 0
                ? `売上 - 経費 - 税金 - 社保 / 共済・iDeCo拠出後 ${fmtYen(Math.floor((t.takeHome - simKyosai - simIdeco) / 12))}/月`
                : '売上 - 経費 - 税金 - 社保',
            },
            { l: '実効税率', v: `${effRate}%`, c: '#ef4444', sub: '社保＋税金 ÷ 売上' },
          ].map(d => (
            <div key={d.l} style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 6 }}>{d.l}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: d.c }}>{d.v}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{d.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.15)', fontSize: 12, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(245,158,11,.15)' }}>RESERVE</span>
          <span>毎月の売上から {fmtYen(reservePlan.monthlyTotal)} を税金用口座に確保してください。</span>
        </div>
      </div>

      {/* KPI breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: '課税所得', val: fmtYen(t.taxableIncome), color: 'var(--text)' },
          { label: '所得税 追加納付', val: fmtYen(t.additionalIncomeTax), color: '#ef4444' },
          { label: '住民税', val: fmtYen(t.residentTax), color: '#f59e0b' },
          { label: '事業税', val: fmtYen(t.businessTax), color: '#8b5cf6' },
          { label: 'スケジュール合計', val: fmtYen(schedTotal), color: '#ef4444' },
          { label: '事業手取り', val: fmtYen(t.takeHome), color: '#22c55e' },
        ].map(k => (
          <div key={k.label} style={{ padding: 14, borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: k.color }}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* 事業税：準委任注記 */}
      <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(139,92,246,.07)', border: '1px solid rgba(139,92,246,.2)', fontSize: 12, color: '#8b5cf6', display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(139,92,246,.15)', whiteSpace: 'nowrap', marginTop: 1 }}>事業税</span>
        <span style={{ lineHeight: 1.6 }}>
          準委任契約のみの場合、請負業（第1種事業）に該当せず<strong>非課税の可能性</strong>があります。
          確定申告書の「事業の種類」欄は <strong>「業務委託（準委任型）コンサルティング」</strong> と記載し、
          準委任であることが明記された契約書を保管しておいてください。所得が290万円以下なら確実に¥0です。
        </span>
      </div>

      {/* Collapsible detail */}
      <button style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: 'var(--text3)', cursor: 'pointer', marginBottom: 12 }} onClick={() => setShowDetail(v => !v)}>
        {showDetail ? '計算詳細を隠す' : '計算詳細を表示'}
      </button>
      {showDetail && (
        <div style={{ fontSize: 13, marginBottom: 24 }}>
          {[
            ['売上（税抜）', fmtYen(t.revenue), ''],
            ['経費', `- ${fmtYen(t.expense)}`, ''],
            ['事業所得', fmtYen(t.income), ''],
            ['青色申告特別控除', `- ${fmtYen(t.blueDeduction)}`, ''],
            ...(t.salaryIncome > 0 ? [
              ['給与収入', fmtYen(t.salaryIncome), ''] as [string, string, string],
              ['給与所得控除', `- ${fmtYen(t.salaryDeduction)}`, ''] as [string, string, string],
              ['給与所得', fmtYen(t.salaryTaxable), ''] as [string, string, string],
            ] : []),
            ['基礎控除', `- ${fmtYen(t.basicDeduction)}`, ''],
            ...(t.socialInsurance > 0 ? [['社会保険料控除', `- ${fmtYen(t.socialInsurance)}`, ''] as [string, string, string]] : []),
            ...(t.mutualAidDeduction > 0 ? [['小規模企業共済等掛金控除', `- ${fmtYen(t.mutualAidDeduction)}`, ''] as [string, string, string]] : []),
            ['課税所得', fmtYen(t.taxableIncome), 'font-weight:600'],
            ['所得税', fmtYen(t.incomeTax), ''],
            ['復興特別所得税', fmtYen(t.reconstructionTax), ''],
            ['所得税 + 復興特別所得税', fmtYen(t.incomeTaxTotal), ''],
            ...(t.withheldTax > 0 ? [['給与で源泉徴収済', `- ${fmtYen(t.withheldTax)}`, ''] as [string, string, string]] : []),
            ...(t.businessWithheld > 0 ? [['事業報酬で源泉徴収済', `- ${fmtYen(t.businessWithheld)}`, ''] as [string, string, string]] : []),
            ['所得税 追加納付', fmtYen(t.additionalIncomeTax), 'font-weight:600'],
            ['住民税の課税所得', fmtYen(t.residentTaxable), ''],
            ['住民税', fmtYen(t.residentTax), ''],
            ['個人事業税', fmtYen(t.businessTax), ''],
            ['税金合計（所得税+住民税+事業税）', fmtYen(t.totalTax), 'color:#ef4444;font-weight:600'],
            ['国保・年金（スケジュール内）', fmtYen(schedTotal - t.totalTax), ''],
            ['スケジュール総額', fmtYen(schedTotal), 'color:#ef4444;font-weight:600'],
            ['実効税率', `${effRate}%`, 'color:var(--text3)'],
            ['事業手取り（売上 - 経費 - 税金 - 社保）', fmtYen(t.takeHome), 'color:#22c55e;font-weight:600'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text2)' }}>{k}</span>
              <span style={{ fontFamily: 'var(--mono)' }}>{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Payment schedule */}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 24, paddingTop: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>納付スケジュール（自動計算）</h3>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>想定売上 {fmtYen(simRev)} / 想定経費 {fmtYen(simExp)} から算出。上の入力を変えると連動します。</div>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{year}年度の所得に対する税金</h4>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>合計: {fmtYen(schedTotal)}</div>
        </div>
        {schedule.map(s => {
          const isPaid = !!paidMap[`${s.type}_${s.due}`]
          if (isPaid) schedPaid += s.amount
          const daysLeft = Math.ceil((new Date(s.due).getTime() - now.getTime()) / 86400000)
          const sc = isPaid ? '#22c55e' : (new Date(s.due) < now ? '#ef4444' : typeColors[s.type] || '#3b82f6')
          const statusText = isPaid ? '支払済' : (new Date(s.due) < now ? '期限超過' : '予定')
          return (
            <div key={`${s.type}_${s.due}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontWeight: 500 }}>{s.label}</span>
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>{s.sub}</span>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ color: 'var(--text3)', fontSize: 12 }}>{fmtDate(s.due)}{!isPaid && daysLeft > 0 ? ` (${daysLeft}日後)` : ''}</span>
                <span style={{ fontWeight: 600, minWidth: 100, textAlign: 'right' }}>{fmtYen(s.amount)}</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: `${sc}20`, color: sc, minWidth: 55, textAlign: 'center' }}>{statusText}</span>
              </div>
            </div>
          )
        })}
        <div style={{ marginTop: 12, padding: '12px 16px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span style={{ color: 'var(--text3)' }}>支払済: {fmtYen(schedPaid)} / {fmtYen(schedTotal)}</span>
          <span style={{ fontWeight: 600, color: '#ef4444' }}>残り: {fmtYen(schedTotal - schedPaid)}</span>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Rules Tab (経費ルール・日々の処理フロー)
// ============================================================

type PaymentMethod = 'biz_card' | 'personal_card' | 'paypay' | 'cash' | 'bank_transfer'
type ReceiptForm = 'electronic' | 'paper' | 'none'
type Frequency = 'one_time' | 'recurring'

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  biz_card: '事業用クレカ',
  personal_card: '個人用クレカ',
  paypay: 'PayPay',
  cash: '現金',
  bank_transfer: '銀行振込',
}
const RECEIPT_LABELS: Record<ReceiptForm, string> = {
  electronic: '電子（PDF/メール/サイトDL）',
  paper: '紙レシート',
  none: '領収書なし',
}
const FREQ_LABELS: Record<Frequency, string> = { one_time: '単発', recurring: '定期（月次/年次）' }

interface QuickRow { method: PaymentMethod; ledger: string; receipt: string; note: string }

const QUICK_TABLE: QuickRow[] = [
  { method: 'biz_card', ledger: '自動連携', receipt: '電子PDF保存', note: 'ほぼ放置OK。月次でカテゴリのみ確認' },
  { method: 'personal_card', ledger: '手動', receipt: '電子PDF保存', note: '「事業主借」で仕訳。月次まとめが楽' },
  { method: 'paypay', ledger: '連携可（要設定）', receipt: 'アプリ履歴DL + CSV保存', note: 'MFでPayPay連携を有効化。領収書はアプリから取得' },
  { method: 'cash', ledger: '手動（出納帳）', receipt: 'レシート撮影（AI-OCR）', note: '7日以内処理。原本は7年保管' },
  { method: 'bank_transfer', ledger: '自動連携', receipt: '電子明細PDF保存', note: '振込明細をMFに連携。手動入力不要' },
]

interface JudgeAction { label: string; severity: 'ok' | 'action' | 'warn' }

function judgeActions(method: PaymentMethod, receipt: ReceiptForm, freq: Frequency): JudgeAction[] {
  const a: JudgeAction[] = []

  // 帳簿入力（青色65万控除前提）
  if (method === 'biz_card' || method === 'bank_transfer') {
    a.push({ label: 'MFクラウドが自動取込 → カテゴリと勘定科目を確認', severity: 'ok' })
    a.push({ label: '青色65万のため、毎月10日までに前月分を仕訳確定', severity: 'warn' })
  } else if (method === 'personal_card') {
    a.push({ label: 'MFクラウドで「経費科目 / 事業主借」として手動仕訳', severity: 'action' })
    a.push({ label: '月末に事業主借の累計を集計 → 翌月に事業用口座へ振替も検討', severity: 'warn' })
  } else if (method === 'paypay') {
    a.push({ label: 'MFクラウドのPayPay連携で自動取込（未設定なら設定）', severity: 'action' })
    a.push({ label: '個人PayPay決済なら「経費科目 / 事業主借」で仕訳', severity: 'warn' })
  } else if (method === 'cash') {
    a.push({ label: '個人財布から払ったなら「経費科目 / 事業主借」で仕訳', severity: 'action' })
    a.push({ label: '事業用現金箱から払ったなら「経費科目 / 現金」で仕訳', severity: 'action' })
  }

  // 証憑保管
  if (receipt === 'electronic') {
    a.push({ label: 'PDF/メールを証憑フォルダに保存（例: 2026-04-22_取引先_金額.pdf）', severity: 'action' })
    a.push({ label: '電帳法: 電子取引データは電子保存義務。印刷保存は不可', severity: 'warn' })
  } else if (receipt === 'paper') {
    a.push({ label: 'MFクラウドアプリでレシート撮影 → AI-OCRで自動仕訳候補', severity: 'action' })
    a.push({ label: '紙原本は7年保管（スキャナ保存の真実性要件を満たせば破棄可）', severity: 'warn' })
  } else {
    a.push({ label: '出金伝票を作成: 日付・金額・摘要・相手先を記録', severity: 'warn' })
    a.push({ label: '会食なら参加者名と人数も明記（1人1万円以下は会議費）', severity: 'warn' })
  }

  // 定期
  if (freq === 'recurring') {
    a.push({ label: 'focus-you の「固定費」タブにも登録（月次支出を可視化）', severity: 'action' })
    a.push({ label: '年間契約の SaaS は「前払費用」計上を検討（節税効果）', severity: 'warn' })
  }

  return a
}

function SeverityBadge({ severity }: { severity: JudgeAction['severity'] }) {
  const map = {
    ok: { bg: 'rgba(34,197,94,0.12)', fg: '#22c55e', label: 'OK' },
    action: { bg: 'rgba(99,102,241,0.12)', fg: '#6366f1', label: 'やる' },
    warn: { bg: 'rgba(245,158,11,0.14)', fg: '#f59e0b', label: '注意' },
  } as const
  const s = map[severity]
  return (
    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.fg, letterSpacing: 0.5, minWidth: 40, textAlign: 'center' }}>
      {s.label}
    </span>
  )
}

function ChoiceRow<T extends string>({ value, setValue, options, labels }: { value: T; setValue: (v: T) => void; options: readonly T[]; labels: Record<T, string> }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => setValue(opt)}
          style={{
            padding: '8px 14px',
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 8,
            border: `1px solid ${value === opt ? 'var(--accent)' : 'var(--border)'}`,
            background: value === opt ? 'var(--accent)' : 'var(--surface2)',
            color: value === opt ? '#fff' : 'var(--text)',
            cursor: 'pointer',
            transition: 'all .15s',
            fontFamily: 'var(--font)',
          }}
        >
          {labels[opt]}
        </button>
      ))}
    </div>
  )
}

// ---- Opening year &青色運用データ ----

interface OpeningNote { title: string; body: string; tone: 'critical' | 'info' | 'tip' }

const OPENING_NOTES: OpeningNote[] = [
  { title: '開業日の仕訳（最重要）', body: '個人→事業用口座の資金投入は「普通預金 / 事業主借」。PCを事業転用したら「工具器具備品 / 事業主借」。開業日(2026/2/X)に全部計上する', tone: 'critical' },
  { title: '開業費の計上', body: '開業前に使った名刺・HP制作費・書籍・セミナー代などは「開業費（繰延資産）」として計上可。最長5年で任意償却できるので、黒字年に経費化して節税', tone: 'tip' },
  { title: '青色承認申請書の期限', body: '開業日から2ヶ月以内（最遅3/15）までに提出必須。提出済みなら2026年分から65万控除適用可', tone: 'info' },
  { title: '白色→青色の切り替え', body: '2025年は白色（雑所得 or 白色事業所得）で申告済み。2026年から青色で、MFクラウドの帳簿は2026/1/1開始で新規セット、開業日(2/X)から仕訳スタート', tone: 'info' },
  { title: '家事按分の設定', body: '自宅で仕事するなら家賃・光熱費・通信費を事業分と個人分で按分。MFクラウドの「各種設定 → 家事按分」で比率を登録しておくと自動按分される', tone: 'tip' },
  { title: 'インボイス登録の判断', body: '年商1000万以下なら任意。法人取引が多いなら登録推奨（先方の仕入税額控除のため）。登録すると消費税申告が必要になるのでトレードオフ', tone: 'info' },
  { title: '事業税は準委任なら非課税の可能性', body: '準委任契約のみ（成果物責任なし・稼働ベース）なら、請負業（第1種）に当たらず個人事業税が非課税になる可能性がある。①確定申告書の「事業の種類」欄に「業務委託（準委任型）コンサルティング」と記載 ②準委任であることが明記された契約書を全件保管 ③請求書は「業務委託料（〇月分稼働XXh）」形式にする。この3点を押さえておけば、通知が来ても異議申し立てができる', tone: 'tip' },
]

interface ChecklistItem { id: string; label: string; priority: 'critical' | 'high' | 'medium' | 'low'; hint?: string }

const OPENING_CHECKLIST: ChecklistItem[] = [
  { id: 'kaigyou_todoke', label: '開業届を税務署に提出', priority: 'critical', hint: '提出済み（2026年）' },
  { id: 'aoiro_shinsei', label: '青色申告承認申請書を提出', priority: 'critical', hint: '提出済み（2026年）' },
  { id: 'kaigyou_date', label: 'MFクラウドで2026年度を選択・開業日を登録', priority: 'critical', hint: '各種設定 → 事業者情報' },
  { id: 'kaigyou_shiwake', label: '開業日の仕訳を入力（事業主借で資産投入）', priority: 'high', hint: '普通預金・工具器具備品など' },
  { id: 'kouza_link', label: '事業用口座をMFクラウドに連携', priority: 'high', hint: 'データ連携 → 金融機関追加' },
  { id: 'card_link', label: '事業用クレカをMFクラウドに連携', priority: 'high' },
  { id: 'paypay_link', label: 'PayPay連携（使うなら）', priority: 'medium' },
  { id: 'kaigyou_hi', label: '開業費を「繰延資産」として計上', priority: 'medium', hint: '開業前の準備費用。領収書を集める' },
  { id: 'kaji_anbun', label: '家事按分の比率を決めて登録', priority: 'medium', hint: '家賃・通信費・電気代など' },
  { id: 'denchou_rule', label: '証憑保管ルールを決定（命名規則・保存先）', priority: 'medium' },
  { id: 'invoice_check', label: 'インボイス登録の要否を判断', priority: 'medium', hint: '取引先が法人中心なら登録推奨' },
  { id: 'prev_expense', label: '2025年分MFデータは放置（白色のため税務影響なし）', priority: 'low' },
  { id: 'jigyouzei_juinin', label: '契約書が「準委任」であることを確認・保管', priority: 'medium', hint: '確定申告書の事業の種類欄: 「業務委託（準委任型）コンサルティング」と記載' },
]

interface RoutineTask { when: string; task: string }

const MONTHLY_ROUTINES: RoutineTask[] = [
  { when: '毎月10日まで', task: '前月分の仕訳を確定（MF自動取込を確認・カテゴリ修正）' },
  { when: '月末', task: 'レシート・証憑の整理（命名規則で保存・MF添付）' },
  { when: '月末', task: '売上請求書の発行・送付・入金確認' },
  { when: '月末', task: '個人カード経費の集計（事業主借の累計確認）' },
]
const QUARTERLY_ROUTINES: RoutineTask[] = [
  { when: '四半期末', task: '売上・経費の進捗確認 → 税金シミュレーション更新' },
  { when: '四半期末', task: '家事按分の比率を見直し（実態と乖離していないか）' },
  { when: '四半期末', task: '未入金請求書の督促確認' },
]
const ANNUAL_ROUTINES: RoutineTask[] = [
  { when: '12月末', task: '棚卸し（在庫あれば）' },
  { when: '12月末', task: '固定資産の減価償却確認' },
  { when: '1月〜2月', task: '支払調書の受領・確認、年間データ締め' },
  { when: '3/15まで', task: '確定申告の提出（e-Taxで65万控除）' },
  { when: '3/31まで', task: '消費税申告（課税事業者のみ）' },
  { when: '6月', task: '住民税1期・個人事業税の納付' },
  { when: '7月', task: '所得税の予定納税1期' },
  { when: '11月', task: '所得税の予定納税2期' },
]

const CHECKLIST_KEY = 'finance_rules_opening_checklist_v1'
const PRIO_COLOR: Record<ChecklistItem['priority'], string> = {
  critical: '#ef4444', high: '#f59e0b', medium: '#6366f1', low: 'var(--text3)',
}
const PRIO_LABEL: Record<ChecklistItem['priority'], string> = {
  critical: '最優先', high: '高', medium: '中', low: '低',
}

function ChecklistSection() {
  const [done, setDone] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {}
    try { return JSON.parse(localStorage.getItem(CHECKLIST_KEY) || '{}') } catch { return {} }
  })
  const toggle = (id: string) => {
    setDone(prev => {
      const next = { ...prev, [id]: !prev[id] }
      try { localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)) } catch { /* noop */ }
      return next
    })
  }
  const completedCount = OPENING_CHECKLIST.filter(it => done[it.id]).length
  const pct = Math.round((completedCount / OPENING_CHECKLIST.length) * 100)
  // 未完了を上、完了済みを下に
  const sorted = [...OPENING_CHECKLIST].sort((a, b) => {
    const ad = done[a.id] ? 1 : 0, bd = done[b.id] ? 1 : 0
    if (ad !== bd) return ad - bd
    const order = { critical: 0, high: 1, medium: 2, low: 3 }
    return order[a.priority] - order[b.priority]
  })

  return (
    <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>今チェックすべきこと（開業初期）</h3>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontVariantNumeric: 'tabular-nums' }}>{completedCount}/{OPENING_CHECKLIST.length} ・ {pct}%</div>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>2026/2開業・青色申告切替に必要な初期設定。進捗はブラウザに保存されます</p>

      <div style={{ height: 4, background: 'var(--bg2)', borderRadius: 999, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#22c55e' : 'var(--accent)', transition: 'width .3s' }} />
      </div>

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map(item => {
          const isDone = !!done[item.id]
          return (
            <li key={item.id}
              onClick={() => toggle(item.id)}
              style={{
                display: 'flex', gap: 12, alignItems: 'flex-start',
                padding: '10px 12px', borderRadius: 8,
                background: isDone ? 'var(--bg2)' : 'transparent',
                border: '1px solid var(--border)',
                cursor: 'pointer', transition: 'all .15s',
                opacity: isDone ? 0.55 : 1,
              }}>
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                border: `1.5px solid ${isDone ? '#22c55e' : 'var(--text3)'}`,
                background: isDone ? '#22c55e' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 12, fontWeight: 700, marginTop: 1,
              }}>{isDone && '✓'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, textDecoration: isDone ? 'line-through' : 'none' }}>{item.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: `${PRIO_COLOR[item.priority]}22`, color: PRIO_COLOR[item.priority], letterSpacing: 0.3 }}>
                    {PRIO_LABEL[item.priority]}
                  </span>
                </div>
                {item.hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>{item.hint}</div>}
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function FinRules() {
  const [method, setMethod] = useState<PaymentMethod>('biz_card')
  const [receipt, setReceipt] = useState<ReceiptForm>('electronic')
  const [freq, setFreq] = useState<Frequency>('one_time')
  const actions = judgeActions(method, receipt, freq)

  const paymentOpts = ['biz_card', 'personal_card', 'paypay', 'cash', 'bank_transfer'] as const
  const receiptOpts = ['electronic', 'paper', 'none'] as const
  const freqOpts = ['one_time', 'recurring'] as const

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 1. クイック早見表 */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>早見表: 支払い方法別にやること</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>基本原則 — MFクラウドをマスター帳簿に、証憑は電子で一括保管、月末に締める</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>
                {['支払い手段', '帳簿入力', '証憑保管', '特記事項'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text3)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {QUICK_TABLE.map(row => (
                <tr key={row.method} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{PAYMENT_LABELS[row.method]}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{row.ledger}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{row.receipt}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text3)', lineHeight: 1.6 }}>{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 2. 判定ウィザード */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>判定: いま何すべき？</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>条件を3つ選ぶと、今日やることが出ます</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, letterSpacing: 0.3 }}>支払い方法</div>
            <ChoiceRow value={method} setValue={setMethod} options={paymentOpts} labels={PAYMENT_LABELS} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, letterSpacing: 0.3 }}>領収書の形式</div>
            <ChoiceRow value={receipt} setValue={setReceipt} options={receiptOpts} labels={RECEIPT_LABELS} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 8, letterSpacing: 0.3 }}>頻度</div>
            <ChoiceRow value={freq} setValue={setFreq} options={freqOpts} labels={FREQ_LABELS} />
          </div>
        </div>

        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '16px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 10, letterSpacing: 0.3 }}>やることリスト</div>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {actions.map((a, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: 'var(--text2)', lineHeight: 1.55 }}>
                <SeverityBadge severity={a.severity} />
                <span>{a.label}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 3. 電帳法ミニガイド */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>電子帳簿保存法（電帳法）の最低ライン</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>2024年1月から完全適用。MFクラウド パーソナルプランの証憑保管機能で要件は満たせる</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {[
            { t: '電子取引は電子保存', d: 'ネット通販・クレカ明細・PayPay領収書など。印刷して紙保存は不可' },
            { t: '検索性3点セット', d: '日付・金額・取引先で検索できる状態にしておく' },
            { t: '保管期間', d: '7年間（青色で欠損金繰越する場合は10年）' },
            { t: '真実性の確保', d: 'タイムスタンプ付与 or 訂正削除履歴が残るシステムで保管（MFは対応）' },
          ].map(item => (
            <div key={item.t} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{item.t}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.55 }}>{item.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. クレカ経費の領収書ガイド */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>クレカ経費：領収書は必要？</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>カード明細は有効な証憑。ただし用途によって追加書類が必要なものがある</p>
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 500 }}>
            <thead>
              <tr>
                {['用途', '明細だけでOK?', '補足'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text3)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { usage: 'コンビニ・消耗品（店頭）', ok: 'ok', note: '明細に店名が出ればOK。レシートは捨ててよい' },
                { usage: '交通費（ICカード等）', ok: 'ok', note: '利用履歴のDLがあればより確実' },
                { usage: 'SaaS・ネット購入', ok: 'ng', note: '電子領収書/請求書PDFを別途保存（電帳法義務）' },
                { usage: 'Amazon・楽天等', ok: 'warn', note: '購入明細ページをPDF保存する。明細だけでは用途不明' },
                { usage: '会食・接待', ok: 'ng', note: '参加者名・人数・目的を摘要欄に記録（必須）' },
                { usage: '高額機器（¥10万超）', ok: 'warn', note: '領収書もあると安全。固定資産として証憑が重要' },
              ].map(row => {
                const badge = row.ok === 'ok'
                  ? { bg: 'rgba(34,197,94,.12)', fg: '#22c55e', label: 'OK' }
                  : row.ok === 'ng'
                  ? { bg: 'rgba(239,68,68,.12)', fg: '#ef4444', label: '要追加' }
                  : { bg: 'rgba(245,158,11,.12)', fg: '#f59e0b', label: '推奨' }
                return (
                  <tr key={row.usage} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{row.usage}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: badge.bg, color: badge.fg }}>{badge.label}</span>
                    </td>
                    <td style={{ padding: '9px 12px', color: 'var(--text3)', lineHeight: 1.6 }}>{row.note}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div style={{ background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 8, padding: '12px 16px', fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: 6 }}>会食・接待だけは別扱い</div>
          <div style={{ color: 'var(--text2)' }}>
            カード明細に金額が出ていても、目的が分からないと経費として弱い。MFの摘要欄に記録する:
          </div>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              '① 参加者名と人数（例: 〇〇社 田中さん 計2名）',
              '② 目的（例: 案件打ち合わせ）',
              '③ 1人¥5,000以下 → 会議費 ／ 超える → 交際費',
            ].map(s => (
              <div key={s} style={{ fontSize: 11, color: 'var(--text3)', paddingLeft: 4 }}>{s}</div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. 開業年の特殊処理 */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>開業年の特殊処理（2026年 / 白色→青色切替）</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>2026/2開業・青色申告1年目の人だけが気にすればいいポイント</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {OPENING_NOTES.map(note => {
            const toneColor = note.tone === 'critical' ? '#ef4444' : note.tone === 'tip' ? '#22c55e' : '#6366f1'
            const toneLabel = note.tone === 'critical' ? '重要' : note.tone === 'tip' ? 'Tip' : 'Info'
            return (
              <div key={note.title} style={{ background: 'var(--bg2)', border: `1px solid var(--border)`, borderLeft: `3px solid ${toneColor}`, borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: `${toneColor}22`, color: toneColor, letterSpacing: 0.5 }}>{toneLabel}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{note.title}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.65 }}>{note.body}</div>
              </div>
            )
          })}
        </div>
      </section>

      {/* 6. 開業初期チェックリスト */}
      <ChecklistSection />

      {/* 7. 青色運用サイクル */}
      <section style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 20 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>運用サイクル（青色申告65万控除キープ）</h3>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)' }}>月次で締める→四半期で見直す→年次で申告。サボると2月末〜3月の自分が泣く</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          {[
            { title: '月次ルーチン', tasks: MONTHLY_ROUTINES, color: '#6366f1' },
            { title: '四半期ルーチン', tasks: QUARTERLY_ROUTINES, color: '#f59e0b' },
            { title: '年次ルーチン', tasks: ANNUAL_ROUTINES, color: '#22c55e' },
          ].map(block => (
            <div key={block.title} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: block.color }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{block.title}</span>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {block.tasks.map((t, i) => (
                  <li key={i} style={{ fontSize: 12, lineHeight: 1.55 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: block.color, letterSpacing: 0.3, marginBottom: 2 }}>{t.when}</div>
                    <div style={{ color: 'var(--text2)' }}>{t.task}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

// ============================================================
// Tax-Saving Strategy Tab (節税戦略)
// ============================================================

type EnrollStatus = 'none' | 'considering' | 'active'

interface TaxSavingInstrument {
  id: string
  name: string
  short: string
  deduction: 'income' | 'expense' | 'none' // 所得控除 / 経費算入 / 控除なし(運用益非課税)
  monthlyMax: number
  annualMax: number
  defaultMonthly: number
  step: number
  liquidity: string
  exitNote: string
  apply: string
  priority: 'top' | 'recommend' | 'minor' | 'skip'
  oneLine: string
}

// 方針: 事業所得600万超 / 流動性を最優先しつつ節税も取る。
// NISA最優先・小規模企業共済で所得控除・iDeCo/経営セーフティ共済は当面見送り、という現在の戦略を反映。
const TS_INSTRUMENTS: TaxSavingInstrument[] = [
  {
    id: 'nisa', name: '新NISA', short: '非課税で運用',
    deduction: 'none', monthlyMax: 300000, annualMax: 3600000, defaultMonthly: 100000, step: 10000,
    liquidity: 'いつでも売却可。枠は翌年復活（流動性◎）',
    exitNote: '運用益・配当が非課税。ただし所得控除はなく、今年の税金は減らない',
    apply: '証券口座でNISA口座を開設（つみたて投資枠120万＋成長投資枠240万＝年360万、生涯1,800万）',
    priority: 'top',
    oneLine: '攻めの非課税運用枠。流動性最優先のあなたの本命',
  },
  {
    id: 'kyosai', name: '小規模企業共済', short: '自分で積む退職金',
    deduction: 'income', monthlyMax: 70000, annualMax: 840000, defaultMonthly: 50000, step: 500,
    liquidity: '貸付制度あり（掛金の範囲で借りられる）。20年未満の任意解約は元本割れ',
    exitNote: '受取は退職所得 or 公的年金等扱いで優遇。廃業・引退時に効く',
    apply: '中小機構（委託団体: 商工会議所・銀行・信用金庫の窓口）で申込',
    priority: 'top',
    oneLine: '掛金が全額所得控除。貸付があるので半分流動的。今年の税金を実際に減らす唯一の柱',
  },
  {
    id: 'fuka', name: '付加年金', short: '月400円の上乗せ',
    deduction: 'income', monthlyMax: 400, annualMax: 4800, defaultMonthly: 400, step: 400,
    liquidity: '国民年金に上乗せ。2年で元が取れる',
    exitNote: '将来「200円×納付月数」が年金に上乗せ。iDeCoをやるなら枠を月1,000円消費する点に注意',
    apply: '市区町村の窓口 or 年金事務所で申込',
    priority: 'minor',
    oneLine: 'iDeCoを見送るなら枠の競合もない。即やる小ワザ',
  },
  {
    id: 'ideco', name: 'iDeCo', short: '私的年金（見送り中）',
    deduction: 'income', monthlyMax: 68000, annualMax: 816000, defaultMonthly: 20000, step: 1000,
    liquidity: '60歳まで引き出し不可（完全ロック）',
    exitNote: '受取は退職所得/公的年金等控除＋運用益も非課税。節税効果は最大だが流動性ゼロ',
    apply: '証券会社で運営管理機関を選び加入（気が変わったら月1万からでも開始可）',
    priority: 'skip',
    oneLine: '満額なら年81.6万の所得控除＝節税は最大。ただ60歳ロックが方針に合わず今は見送り',
  },
  {
    id: 'safety', name: '経営セーフティ共済', short: '倒産防止共済（見送り中）',
    deduction: 'expense', monthlyMax: 200000, annualMax: 2400000, defaultMonthly: 100000, step: 5000,
    liquidity: '40ヶ月以上で解約手当金100%。それ未満は元本割れ',
    exitNote: '解約金は受取時に全額が事業所得として課税（＝節税でなく繰延）。赤字年・廃業年に解約する出口設計が前提',
    apply: '中小機構（金融機関・商工会議所の窓口）。掛金は経費算入の明細書を確定申告に添付',
    priority: 'skip',
    oneLine: '掛金は全額経費だが出口で課税される繰延。流動性重視の方針には旨みが薄い',
  },
]

const TS_DEDUCTION_META: Record<TaxSavingInstrument['deduction'], { label: string; color: string }> = {
  income: { label: '所得控除', color: '#22c55e' },
  expense: { label: '経費算入', color: '#6366f1' },
  none: { label: '運用益非課税', color: '#06b6d4' },
}

const TS_PRIORITY_META: Record<TaxSavingInstrument['priority'], { label: string; color: string }> = {
  top: { label: '最優先', color: '#ef4444' },
  recommend: { label: '推奨', color: '#f59e0b' },
  minor: { label: '小ワザ', color: '#6366f1' },
  skip: { label: '見送り中', color: 'var(--text3)' },
}

const TS_STATUS_LABEL: Record<EnrollStatus, string> = { none: '未加入', considering: '検討中', active: '加入' }

// 毎月積立の将来価値（年利 annualRatePct%、years 年）
function futureValueMonthly(monthly: number, annualRatePct: number, years: number): number {
  const n = years * 12
  const mr = annualRatePct / 100 / 12
  if (mr === 0) return monthly * n
  return Math.round(monthly * ((Math.pow(1 + mr, n) - 1) / mr))
}

const TS_KEY = 'finance_tax_saving_v1'
interface TSEntry { status: EnrollStatus; monthly: number; startMonth?: number }

const emptyTSEntry = (): TSEntry => ({ status: 'none', monthly: 0, startMonth: 1 })

function loadTaxSavingState(): Record<string, TSEntry> {
  let savingState: Record<string, TSEntry> = {}
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(TS_KEY) || '{}')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      savingState = parsed as Record<string, TSEntry>
    }
  } catch {
    savingState = {}
  }

  let migrated = false
  const legacyEntries = [
    ['finTax.simKyosai', 'kyosai'],
    ['finTax.simIdeco', 'ideco'],
  ] as const
  legacyEntries.forEach(([storageKey, instrumentId]) => {
    const storedAnnual = Number(localStorage.getItem(storageKey) || 0)
    if (storedAnnual > 0 && savingState[instrumentId]?.status !== 'active') {
      savingState[instrumentId] = {
        status: 'active',
        monthly: Math.floor(storedAnnual / 12),
        startMonth: 1,
      }
      migrated = true
    }
    localStorage.removeItem(storageKey)
  })

  if (migrated) localStorage.setItem(TS_KEY, JSON.stringify(savingState))
  return savingState
}

interface TaxSavingBaseInputs {
  revenue: number
  expense: number
  salaryIncome: number
  socialInsurance: number
  blueDeduction: number
}

function FinTaxSaving() {
  const year = new Date().getFullYear()
  const [taxableIncome, setTaxableIncome] = useState<number | null>(null)
  const [baseTaxInputs, setBaseTaxInputs] = useState<TaxSavingBaseInputs | null>(null)
  const [effRate, setEffRate] = useState<number>(() => Number(localStorage.getItem('finTaxSaving.effRate')) || 0.33)
  const [rateAuto, setRateAuto] = useState(() => localStorage.getItem('finTaxSaving.rateAuto') !== 'false')
  const [state, setState] = useState<Record<string, TSEntry>>(loadTaxSavingState)
  const [simYears, setSimYears] = useState<number>(() => Number(localStorage.getItem('finTaxSaving.simYears')) || 30)
  const [idecoReturn, setIdecoReturn] = useState<number>(() => { const v = Number(localStorage.getItem('finTaxSaving.idecoReturn')); return v > 0 ? v : 3 })
  const [drawYears, setDrawYears] = useState<number>(() => Number(localStorage.getItem('finTaxSaving.drawYears')) || 25)
  const [pensionAnnual, setPensionAnnual] = useState<number>(() => Number(localStorage.getItem('finTaxSaving.pensionAnnual')) || 830000)
  const [age, setAge] = useState<number>(() => Number(localStorage.getItem('finTaxSaving.age')) || 32)

  useEffect(() => { localStorage.setItem(TS_KEY, JSON.stringify(state)) }, [state])
  useEffect(() => { localStorage.setItem('finTaxSaving.effRate', String(effRate)) }, [effRate])
  useEffect(() => { localStorage.setItem('finTaxSaving.rateAuto', String(rateAuto)) }, [rateAuto])
  useEffect(() => { localStorage.setItem('finTaxSaving.simYears', String(simYears)) }, [simYears])
  useEffect(() => { localStorage.setItem('finTaxSaving.idecoReturn', String(idecoReturn)) }, [idecoReturn])
  useEffect(() => { localStorage.setItem('finTaxSaving.drawYears', String(drawYears)) }, [drawYears])
  useEffect(() => { localStorage.setItem('finTaxSaving.pensionAnnual', String(pensionAnnual)) }, [pensionAnnual])
  useEffect(() => { localStorage.setItem('finTaxSaving.age', String(age)) }, [age])

  useEffect(() => {
    Promise.all([
      supabase.from('invoices').select('amount,invoice_date,paid_date'),
      supabase.from('expenses').select('amount,expense_date,is_deductible').gte('expense_date', `${year}-01-01`).lte('expense_date', `${year}-12-31`),
    ]).then(([invRes, expRes]) => {
      const yStart = `${year}-01-01`, yEnd = `${year}-12-31`
      const invoices = (invRes.data || []).filter((i: { invoice_date: string; paid_date?: string | null }) => { const d = revenueDate(i); return d >= yStart && d <= yEnd })
      const rev = invoices.reduce((s: number, i: { amount: number }) => s + i.amount, 0)
      const monthSet: Record<string, boolean> = {}
      invoices.forEach((i: { invoice_date: string; paid_date?: string | null }) => { monthSet[revenueDate(i).substring(0, 7)] = true })
      const mwd = Math.max(Object.keys(monthSet).length, 1)
      const annualizationMonths = Number(localStorage.getItem('finTax.annualizationMonths') || 12)
      const projRev = Math.round(rev / mwd * annualizationMonths)
      const expenses = (expRes.data || []).filter((e: { is_deductible?: boolean }) => e.is_deductible !== false)
      const expSum = expenses.reduce((s: number, e: { amount: number }) => s + e.amount, 0)
      const expMonthSet = new Set(expenses.map((e: { expense_date: string }) => e.expense_date.substring(0, 7)))
      const expenseMonths = Math.max(expMonthSet.size, 1)
      const projExp = Math.round(expSum / expenseMonths * annualizationMonths)
      const inputs = {
        revenue: projRev,
        expense: projExp,
        salaryIncome: Number(localStorage.getItem('finTax.simSalary') || 0),
        socialInsurance: Number(localStorage.getItem('finTax.simSocial') || 0),
        blueDeduction: Number(localStorage.getItem('finTax.blueDeduction') ?? 650_000),
      }
      const t = calcTax(inputs)
      setBaseTaxInputs(inputs)
      setTaxableIncome(t.taxableIncome)
    })
  }, [year])

  useEffect(() => {
    // 自動の実効限界税率は、掛金適用前のベース税額（T0）の課税所得から算出する。
    if (rateAuto && taxableIncome != null) {
      setEffRate(Math.min(0.55, marginalIncomeRate(taxableIncome) + 0.10))
    }
  }, [rateAuto, taxableIncome])

  const get = (id: string): TSEntry => state[id] || emptyTSEntry()
  const setEntry = (id: string, patch: Partial<TSEntry>) => {
    setState(current => ({
      ...current,
      [id]: { ...(current[id] || emptyTSEntry()), ...patch },
    }))
  }

  const annualOf = (inst: TaxSavingInstrument, e: TSEntry) => annualContribution(e.monthly, e.startMonth, inst.annualMax)
  const savingOf = (inst: TaxSavingInstrument, e: TSEntry) => inst.deduction === 'none' ? 0 : Math.round(annualOf(inst, e) * effRate)

  const activeInsts = TS_INSTRUMENTS.filter(i => get(i.id).status === 'active')
  const activeContribTotal = activeInsts.reduce((s, i) => s + annualOf(i, get(i.id)), 0)
  const activeDeductible = activeInsts.reduce((s, i) => s + (i.deduction === 'none' ? 0 : annualOf(i, get(i.id))), 0)
  const taxBurden = (tax: ReturnType<typeof calcTax>) => tax.incomeTaxTotal + tax.residentTax + tax.businessTax
  const exactSaving = (annualFor: (instrument: TaxSavingInstrument) => number) => {
    if (!baseTaxInputs) return 0
    let mutualAidDeduction = 0
    let additionalSocialInsurance = 0
    let additionalExpense = 0
    TS_INSTRUMENTS.forEach(inst => {
      const annual = annualFor(inst)
      if (inst.id === 'kyosai' || inst.id === 'ideco') mutualAidDeduction += annual
      if (inst.id === 'fuka') additionalSocialInsurance += annual
      if (inst.id === 'safety') additionalExpense += annual
    })
    const baseTax = calcTax(baseTaxInputs)
    const appliedTax = calcTax({
      ...baseTaxInputs,
      expense: baseTaxInputs.expense + additionalExpense,
      socialInsurance: baseTaxInputs.socialInsurance + additionalSocialInsurance,
      mutualAidDeduction,
    })
    return Math.max(0, taxBurden(baseTax) - taxBurden(appliedTax))
  }
  const currentSaving = exactSaving(inst => get(inst.id).status === 'active' ? annualOf(inst, get(inst.id)) : 0)
  const maxSaving = exactSaving(inst => inst.annualMax)

  // 将来シミュレーション（小規模=予定利率1%固定 / iDeCo・NISA=運用利回り可変）
  const NISA_CAP = 18000000
  const simMonthly = (id: string): number => {
    const e = state[id]
    return e ? e.monthly : (TS_INSTRUMENTS.find(i => i.id === id)!.defaultMonthly)
  }
  const simRows = (['kyosai', 'ideco', 'nisa'] as const).map(id => {
    const inst = TS_INSTRUMENTS.find(i => i.id === id)!
    const monthly = simMonthly(id)
    const ratePct = id === 'kyosai' ? 1 : idecoReturn
    let principal = monthly * 12 * simYears
    let future = futureValueMonthly(monthly, ratePct, simYears)
    let capped = false
    if (id === 'nisa' && monthly > 0) {
      const fillYears = Math.min(simYears, Math.floor(NISA_CAP / (monthly * 12)))
      if (fillYears < simYears) {
        capped = true
        principal = monthly * 12 * fillYears
        future = Math.round(futureValueMonthly(monthly, ratePct, fillYears) * Math.pow(1 + ratePct / 100, simYears - fillYears))
      }
    }
    const gain = Math.max(0, future - principal)
    const taxSave = inst.deduction === 'none'
      ? Math.round(gain * 0.20) // NISA: 運用益が非課税で浮く税金
      : Math.round(Math.min(monthly * 12, inst.annualMax) * effRate * simYears)
    return { id, name: inst.name, monthly, ratePct, principal, future, gain, taxSave, capped, deduction: inst.deduction, monthlyMax: inst.monthlyMax, step: inst.step }
  })
  const simTaxSaveTotal = simRows.reduce((s, r) => s + r.taxSave, 0)
  const simGainTotal = simRows.reduce((s, r) => s + r.gain, 0)
  const simAssetTotal = simRows.reduce((s, r) => s + r.future, 0)
  const simPaidTotal = simRows.reduce((s, r) => s + r.principal, 0)
  const simMonthlyTotal = simRows.reduce((s, r) => s + r.monthly, 0)
  const simBenefitTotal = simTaxSaveTotal + simGainTotal
  const retireMonthlyPension = Math.round(pensionAnnual / 12)
  const retireMonthlyDraw = Math.round(simAssetTotal / drawYears / 12)
  const retireMonthly = retireMonthlyPension + retireMonthlyDraw
  const drawEndAge = 65 + drawYears
  // 「全額を特定口座(株)で運用」との比較
  const CAP_GAIN_TAX = 0.20315
  const taxablePrincipal = simMonthlyTotal * 12 * simYears
  const taxableFV = futureValueMonthly(simMonthlyTotal, idecoReturn, simYears)
  const taxableGain = Math.max(0, taxableFV - taxablePrincipal)
  const taxableNet = Math.round(taxablePrincipal + taxableGain * (1 - CAP_GAIN_TAX))
  const simDeductionRefund = simRows.reduce((s, r) => s + (r.deduction !== 'none' ? r.taxSave : 0), 0)
  const schemeTotalValue = simAssetTotal + simDeductionRefund
  const schemeWins = schemeTotalValue >= taxableNet

  const kpis = [
    { l: '実効限界税率', v: `${Math.round(effRate * 100)}%`, c: '#ef4444', sub: '所得税＋住民税の限界' },
    { l: '加入中の年間掛金', v: fmtYen(activeContribTotal), c: 'var(--accent)', sub: `控除対象 ${fmtYen(activeDeductible)}` },
    { l: '今年の推定節税額', v: fmtYen(currentSaving), c: '#22c55e', sub: '加入中の所得控除＋経費算入分' },
    { l: 'フル活用時の節税額', v: fmtYen(maxSaving), c: 'var(--amber)', sub: '全制度を上限まで使った場合' },
  ]

  return (
    <div>
      {/* 方針バナー */}
      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)', borderRadius: 10, padding: '16px 18px', marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>現在の方針 — 事業所得600万超 / 流動性を最優先しつつ節税も取る</div>
        <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text2)', lineHeight: 1.8 }}>
          <li><strong>新NISA</strong> で非課税の運用枠（年360万）を埋める — 攻めの本命。いつでも引き出せる</li>
          <li><strong>小規模企業共済</strong> で所得控除を取る — 貸付があるので半分流動的。<strong>今年の税金を実際に減らす唯一の柱</strong></li>
          <li><strong>付加年金</strong>（月400円）だけは入れておく小ワザ</li>
          <li><strong>iDeCo・経営セーフティ共済</strong> は当面見送り（60歳ロック／出口課税の繰延が方針に合わない）</li>
        </ol>
        <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.18)', fontSize: 11.5, color: '#f59e0b', lineHeight: 1.6 }}>
          注意: NISAは「節税（所得控除）」ではなく非課税運用。今年の税額を直接下げるのは小規模企業共済だけです。「とにかく税金を減らす」を強めたくなったら iDeCo を少額から戻すのが最短。
        </div>
      </div>

      {/* 限界税率コントロール */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: 'var(--text2)' }}>実効限界税率</label>
        <button onClick={() => setRateAuto(v => !v)} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)', background: rateAuto ? 'var(--accent)' : 'var(--surface2)', color: rateAuto ? '#fff' : 'var(--text2)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
          {rateAuto ? '自動（実績から推定）' : '手動'}
        </button>
        <input type="range" min={0.15} max={0.55} step={0.01} value={effRate} disabled={rateAuto} onChange={e => setEffRate(parseFloat(e.target.value))} style={{ width: 180, accentColor: 'var(--accent)', opacity: rateAuto ? 0.5 : 1 }} />
        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--mono)', color: '#ef4444' }}>{Math.round(effRate * 100)}%</span>
        {taxableIncome != null && <span style={{ fontSize: 11, color: 'var(--text3)' }}>（推定課税所得 {fmtYen(taxableIncome)} → 所得税{Math.round(marginalIncomeRate(taxableIncome) * 100)}%＋住民税10%）</span>}
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 28 }}>
        {kpis.map(d => (
          <div key={d.l} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '16px 18px' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{d.l}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: d.c }}>{d.v}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>{d.sub}</div>
          </div>
        ))}
      </div>

      {/* 制度カード */}
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>制度トラッカー</h3>
      <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 16px' }}>加入状況と月額掛金を入れると、上のKPIに節税額が反映されます（保存はこの端末のブラウザ）</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {TS_INSTRUMENTS.map(inst => {
          const e = get(inst.id)
          const ded = TS_DEDUCTION_META[inst.deduction]
          const pri = TS_PRIORITY_META[inst.priority]
          const annual = annualOf(inst, e)
          const saving = savingOf(inst, e)
          const isOpen = e.status !== 'none'
          return (
            <div key={inst.id} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, opacity: inst.priority === 'skip' && e.status === 'none' ? 0.72 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{inst.name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>{inst.short}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: `${ded.color}20`, color: ded.color }}>{ded.label}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: `${pri.color}20`, color: pri.color }}>{pri.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.55 }}>{inst.oneLine}</div>
                </div>
                {/* status selector */}
                <div style={{ display: 'flex', gap: 4 }}>
                  {(['none', 'considering', 'active'] as EnrollStatus[]).map(st => (
                    <button key={st} onClick={() => setEntry(inst.id, { status: st, monthly: st !== 'none' && e.monthly === 0 ? inst.defaultMonthly : e.monthly })}
                      style={{ padding: '5px 10px', fontSize: 11, fontWeight: 500, borderRadius: 6, border: `1px solid ${e.status === st ? 'var(--accent)' : 'var(--border)'}`, background: e.status === st ? 'var(--accent)' : 'var(--surface2)', color: e.status === st ? '#fff' : 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {TS_STATUS_LABEL[st]}
                    </button>
                  ))}
                </div>
              </div>

              {isOpen && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                    <label style={{ fontSize: 12, color: 'var(--text3)', minWidth: 70 }}>月額掛金</label>
                    <input type="number" value={e.monthly} step={inst.step} min={0} max={inst.monthlyMax} onChange={ev => setEntry(inst.id, { monthly: Math.min(inst.monthlyMax, parseInt(ev.target.value) || 0) })}
                      style={{ width: 120, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, fontFamily: 'var(--mono)', background: 'var(--bg2)', color: 'var(--text)' }} />
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>/ 月（上限 {fmtYen(inst.monthlyMax)}）</span>
                    <button onClick={() => setEntry(inst.id, { monthly: inst.monthlyMax })} style={{ padding: '4px 10px', fontSize: 11, borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer' }}>上限</button>
                    {e.status === 'active' && (
                      <>
                        <label style={{ fontSize: 12, color: 'var(--text3)' }}>今年の加入開始月</label>
                        <select value={e.startMonth ?? 1} onChange={ev => setEntry(inst.id, { startMonth: Number(ev.target.value) })}
                          style={{ width: 80, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--mono)', background: 'var(--bg2)', color: 'var(--text)' }}>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => <option key={month} value={month}>{month}月</option>)}
                        </select>
                      </>
                    )}
                  </div>
                  <input type="range" min={0} max={inst.monthlyMax} step={inst.step} value={e.monthly} onChange={ev => setEntry(inst.id, { monthly: parseInt(ev.target.value) })} style={{ width: '100%', accentColor: 'var(--accent)', marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                    <span style={{ color: 'var(--text3)' }}>当年掛金 <span style={{ fontWeight: 600, color: 'var(--text)' }}>{fmtYen(annual)}</span></span>
                    <span style={{ color: 'var(--text3)' }}>推定節税額 <span style={{ fontWeight: 700, color: saving > 0 ? '#22c55e' : 'var(--text3)' }}>{saving > 0 ? `${fmtYen(saving)}/年` : '— (控除なし)'}</span></span>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 8, fontSize: 11, color: 'var(--text3)', lineHeight: 1.55 }}>
                <div><span style={{ fontWeight: 600, color: 'var(--text2)' }}>流動性: </span>{inst.liquidity}</div>
                <div><span style={{ fontWeight: 600, color: 'var(--text2)' }}>出口: </span>{inst.exitNote}</div>
                <div><span style={{ fontWeight: 600, color: 'var(--text2)' }}>申込: </span>{inst.apply}</div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 将来シミュレーション */}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 28, paddingTop: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>将来シミュレーション（結局いくら得するか＋60歳以降）</h3>
        <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 14px' }}>月額は下の表で直接編集できます（小規模いくら・iDeCoいくら・NISAいくら、を自由に）。小規模は予定利率1%固定、iDeCo・NISAは運用利回りで複利。プリセットで丸ごと差し替えも可。</p>

        {/* プリセット */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { label: '流動性重視（現方針）', kyosai: 50000, ideco: 0, nisa: 50000 },
            { label: 'バランス', kyosai: 50000, ideco: 20000, nisa: 50000 },
            { label: '節税MAX', kyosai: 70000, ideco: 68000, nisa: 50000 },
          ].map(p => (
            <button key={p.label} onClick={() => setState(s => ({
              ...s,
              kyosai: { status: 'active', monthly: p.kyosai },
              ideco: { status: p.ideco > 0 ? 'active' : 'none', monthly: p.ideco },
              nisa: { status: 'active', monthly: p.nisa },
            }))} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text2)', cursor: 'pointer' }}>
              {p.label}
            </button>
          ))}
        </div>

        {/* inputs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: '10px 24px', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>現在の年齢</label>
            <input type="number" min={18} max={70} value={age} onChange={e => setAge(parseInt(e.target.value) || 0)} style={{ width: 64, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--mono)', background: 'var(--bg2)', color: 'var(--text)' }} />
            <button onClick={() => setSimYears(Math.max(1, 60 - age))} style={{ padding: '5px 9px', fontSize: 11, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text2)', cursor: 'pointer', whiteSpace: 'nowrap' }}>積立を60歳まで({Math.max(0, 60 - age)}年)</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>積立年数</label>
            <input type="range" min={5} max={40} step={1} value={simYears} onChange={e => setSimYears(parseInt(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', minWidth: 44, textAlign: 'right' }}>{simYears}年</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>運用利回り</label>
            <input type="range" min={0} max={7} step={0.5} value={idecoReturn} onChange={e => setIdecoReturn(parseFloat(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: '#06b6d4', minWidth: 44, textAlign: 'right' }}>{idecoReturn}%</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>取り崩し年数</label>
            <input type="range" min={10} max={35} step={1} value={drawYears} onChange={e => setDrawYears(parseInt(e.target.value))} style={{ flex: 1, accentColor: 'var(--accent)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', minWidth: 44, textAlign: 'right' }}>{drawYears}年</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)', minWidth: 100 }}>国民年金(年)</label>
            <input type="number" step={10000} value={pensionAnnual} onChange={e => setPensionAnnual(parseInt(e.target.value) || 0)} style={{ width: 110, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--mono)', background: 'var(--bg2)', color: 'var(--text)' }} />
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>満額約83万</span>
          </div>
        </div>

        {/* table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 580 }}>
            <thead>
              <tr>
                {['制度', '月額（編集可）', '総支払額', `${simYears}年後の評価額`, '運用益', '税メリット'].map(h => (
                  <th key={h} style={{ textAlign: h === '制度' ? 'left' : 'right', padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text3)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {simRows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.name}<span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>利{r.ratePct}%</span>{r.capped && <span style={{ fontSize: 9, color: '#f59e0b', marginLeft: 6 }}>枠1,800万で打止</span>}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                    <input type="number" value={r.monthly} step={r.step} min={0} max={r.monthlyMax}
                      onChange={e => setEntry(r.id, { monthly: Math.min(r.monthlyMax, parseInt(e.target.value) || 0) })}
                      style={{ width: 100, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'var(--mono)', background: 'var(--bg2)', color: 'var(--text)', textAlign: 'right' }} />
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{fmtYen(r.principal)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: '#06b6d4' }}>{fmtYen(r.future)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', color: '#22c55e' }}>+{fmtYen(r.gain)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: '#22c55e' }}>{fmtYen(r.taxSave)}{r.deduction === 'none' && <span style={{ fontSize: 9, color: 'var(--text3)', display: 'block' }}>非課税分</span>}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 700 }}>合計</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700 }}>{fmtYen(simMonthlyTotal)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--text2)' }}>{fmtYen(simPaidTotal)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: '#06b6d4' }}>{fmtYen(simAssetTotal)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: '#22c55e' }}>+{fmtYen(simGainTotal)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: 700, color: '#22c55e' }}>{fmtYen(simTaxSaveTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 得サマリー */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginTop: 16 }}>
          {[
            { l: '総支払額', v: fmtYen(simPaidTotal), c: 'var(--text2)', sub: `毎月 ${fmtYen(simMonthlyTotal)} × ${simYears}年` },
            { l: '結局いくら得？', v: fmtYen(simBenefitTotal), c: '#22c55e', sub: `税メリット ${fmtYen(simTaxSaveTotal)} ＋ 運用益 ${fmtYen(simGainTotal)}` },
            { l: '60歳時点の自助資産', v: fmtYen(simAssetTotal), c: '#06b6d4', sub: `総支払額＋運用益（${simYears}年）` },
            { l: '65歳〜の月収目安', v: `${fmtYen(retireMonthly)}/月`, c: 'var(--accent)', sub: `国民年金 ${fmtYen(retireMonthlyPension)}（終身）＋ 取崩 ${fmtYen(retireMonthlyDraw)}（${drawYears}年で資産は尽きる）` },
          ].map(d => (
            <div key={d.l} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 6 }}>{d.l}</div>
              <div style={{ fontSize: 21, fontWeight: 700, color: d.c }}>{d.v}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, lineHeight: 1.4 }}>{d.sub}</div>
            </div>
          ))}
        </div>

        {/* 老後の収入の流れ（資産は減る） */}
        <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 8, background: 'rgba(80,70,229,.06)', border: '1px solid var(--border)', fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, color: 'var(--accent)', marginBottom: 8 }}>老後の収入の流れ — 自助資産は取り崩すと減ります</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ minWidth: 96, fontWeight: 600, color: 'var(--text2)' }}>65〜{drawEndAge}歳</span>
              <span style={{ color: 'var(--text)' }}>月 <strong style={{ color: 'var(--accent)' }}>{fmtYen(retireMonthly)}</strong>　＝ 国民年金 {fmtYen(retireMonthlyPension)}（終身・減らない）＋ 自助資産の取り崩し {fmtYen(retireMonthlyDraw)}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ minWidth: 96, fontWeight: 600, color: 'var(--text2)' }}>{drawEndAge}歳〜</span>
              <span style={{ color: 'var(--text3)' }}>月 <strong>{fmtYen(retireMonthlyPension)}</strong>　＝ 自助資産は使い切り、国民年金のみ（終身）</span>
            </div>
          </div>
          <div style={{ marginTop: 10, color: 'var(--text3)' }}>
            「65歳〜の月収」= 国民年金（死ぬまで一定）＋ 自助資産 {fmtYen(simAssetTotal)} ÷ {drawYears}年 ÷ 12。資産は取り崩すほど減り、{drawYears}年で使い切る前提です（運用しながら取り崩せばもう少し延びます）。長持ちさせるなら取り崩し年数を延ばす（月額は下がる）か、資産を増やす。
          </div>
        </div>

        <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 8, background: 'rgba(34,197,94,.07)', border: '1px solid rgba(34,197,94,.2)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, color: '#22c55e', marginBottom: 4 }}>総支払 {fmtYen(simPaidTotal)} → 60歳で {fmtYen(simAssetTotal)}。さらに税で {fmtYen(simTaxSaveTotal)} 得（得の合計 {fmtYen(simBenefitTotal)}）</div>
          税メリットは「所得控除で浮いた税金（小規模・iDeCo）＋ 運用益が非課税で浮いた税金（NISA）」。受取時も<strong>退職所得控除</strong>で一括ならほぼ非課税。
          <span style={{ color: 'var(--text3)' }}> ※ 小規模とiDeCoの一時金を同年に受けると退職所得控除が重複しない（5年/19年ルール）。受取年をずらすのが定石。</span>
        </div>

        {/* vs 特定口座（株・課税） */}
        <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 12, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>「全部 株（特定口座）」と比べると？ 利回り{idecoReturn}% / 株の税 {(CAP_GAIN_TAX * 100).toFixed(3)}%</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12, marginBottom: 10 }}>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>この設計（節税スキーム）</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: '#22c55e' }}>{fmtYen(schemeTotalValue)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>60歳資産 {fmtYen(simAssetTotal)} ＋ 控除の還付 {fmtYen(simDeductionRefund)}</div>
            </div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>全額を特定口座の株</div>
              <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--text2)' }}>{fmtYen(taxableNet)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>利益に{(CAP_GAIN_TAX * 100).toFixed(1)}%課税・控除なし</div>
            </div>
          </div>
          <div style={{ fontWeight: 700, color: schemeWins ? '#22c55e' : '#f59e0b', marginBottom: 8 }}>
            {schemeWins ? `→ 節税スキームが ${fmtYen(schemeTotalValue - taxableNet)} 得` : `→ 全額を株(特定口座)にした方が ${fmtYen(taxableNet - schemeTotalValue)} 得`}
          </div>
          <div style={{ color: 'var(--text3)' }}>
            ※ NISA・iDeCoは「株を“非課税の箱”に入れる」もの。<strong>株 vs 節税は対立しません</strong>（同じ株でも箱に入れた方が必ず得）。1%固定の<strong>小規模だけ</strong>が株と迷う対象で、株の想定利回りが約2〜3%を超えると成長面では株が上回ります。ただし小規模は所得控除＋元本確保＋貸付という別の役割。利回りスライダーを上げると逆転点が見えます。
          </div>
        </div>
      </div>

      {/* 国民年金カード */}
      <div style={{ marginTop: 24, background: 'var(--surface2)', border: '1px solid var(--border)', borderLeft: '3px solid #14b8a6', borderRadius: 10, padding: 18 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>国民年金：払う必要ある？ いくらもらえる？</h3>
        <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 14px' }}>個人事業主は第1号被保険者。厚生年金（2階）がない分、土台＋自助の設計が重要</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 14 }}>
          {[
            { l: '満額（40年納付）', v: '月約6.9万', sub: '年約83万・終身（死ぬまで）' },
            { l: '保険料', v: '月約1.7万', sub: '年約20万・40年で総額約816万' },
            { l: '元が取れる', v: '約10年', sub: '65歳受給→75歳で回収。平均寿命まででプラス' },
            { l: '保険料の節税', v: '年約6万', sub: '全額が社会保険料控除（税率30%時）' },
          ].map(d => (
            <div key={d.l} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{d.l}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#14b8a6' }}>{d.v}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{d.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { tone: '#ef4444', label: '結論', text: '払う必要あり＝法的義務（強制加入）。「もらえないから払わない」は選択肢ではなく未納。' },
            { tone: '#ef4444', label: '未納の罠', text: '一番怖いのは障害年金・遺族年金が出なくなること。若い今、事故や病気で障害を負っても保障されない。将来の年金減・差押えのリスクも。' },
            { tone: '#6366f1', label: 'もらえない説', text: '1円もゼロは誤り（給付費の1/2は国庫負担＋積立金約200兆）。ただし給付水準は今より下がる方向。だからiDeCo・小規模・NISAで上乗せするのが正解。' },
            { tone: '#22c55e', label: '節税の小ワザ', text: '2年前納で約1.6万円割引＋前納年に全額控除。付加年金（月400円→200円×納付月数が一生上乗せ・2年で元）も併用可。' },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: `${r.tone}20`, color: r.tone, whiteSpace: 'nowrap', marginTop: 1, minWidth: 64, textAlign: 'center' }}>{r.label}</span>
              <span style={{ color: 'var(--text2)' }}>{r.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 免責 */}
      <div style={{ marginTop: 20, fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
        ※ 節税額・受取額は概算です。節税は「掛金 × 実効限界税率」、受取は単純複利で、控除による税率帯の変動・運用変動・手数料・年金改定は反映していません。年金額は2024〜25年度の満額を基準。正確な判断は確定申告前に税理士・年金事務所に確認してください。
      </div>
    </div>
  )
}

// ============================================================
// ApiCosts (embedded shortcut)
// ============================================================

import { ApiCosts } from './ApiCosts'

// ============================================================
// Main Finance Component
// ============================================================

const TABS = ['overview', 'rules', 'subscriptions', 'wishlist', 'api-costs', 'projects', 'invoices', 'expenses', 'tax', 'tax-saving'] as const
type TabId = typeof TABS[number]
const TAB_LABELS: Record<TabId, string> = { overview: '概要', rules: '経費ルール', subscriptions: '固定費', wishlist: 'ほしい物', 'api-costs': 'APIコスト', projects: '案件', invoices: '請求書', expenses: '経費', tax: '税金', 'tax-saving': '節税' }

export function Finance() {
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const q = qs?.get('tab')
    return (q && (TABS as readonly string[]).includes(q) ? (q as TabId) : 'overview')
  })

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <FinOverview />
      case 'rules': return <FinRules />
      case 'subscriptions': return <FinSubscriptions />
      case 'wishlist': return <FinWishlist />
      case 'api-costs': return <ApiCosts />
      case 'projects': return <FinProjects />
      case 'invoices': return <FinInvoices />
      case 'expenses': return <FinExpenses />
      case 'tax': return <FinTax />
      case 'tax-saving': return <FinTaxSaving />
    }
  }

  return (
    <div className="page">
      <PageHeader title="Finance" description="売上・経費・稼働時間・税金の管理" />

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, margin: '24px 0 20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button
            key={tab}
            style={{ background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font)', fontWeight: 500, borderBottom: tab === activeTab ? '2px solid var(--accent)' : '2px solid transparent', color: tab === activeTab ? 'var(--text)' : 'var(--text3)', transition: 'all .2s', whiteSpace: 'nowrap' }}
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  )
}
